/**
 * Lossless MP3 trim at frame boundaries.
 *
 * Why this exists — bug-report kocevnik05@gmail.com 2026-05-02 (Opera Mobile 97
 * / Android 10 / 4 GB / 11 MB MP3): trim hung at 65% then "reset". Root cause:
 * MediaPipeline.audio.process did decode→render→encode for a simple mp3→mp3
 * trim, allocating ~423 MB Float32 PCM, which Android's low-memory killer
 * silently zombiefies the lamejs Worker on. The cure is to not allocate
 * the buffer in the first place — MP3 is frame-based, simple cuts on frame
 * boundaries are lossless and bytes-level.
 *
 * What this module does:
 *   - Parses MPEG audio frame headers (MPEG 1/2/2.5, Layer 1/2/3, CBR + VBR).
 *   - Skips ID3v2 (variable-length tag at start) and detects ID3v1 (128 B at
 *     end). The original ID3v2 is preserved; ID3v1 is preserved if present.
 *   - Walks frame headers, accumulating (offset, duration) per frame.
 *   - Snaps user start/end to the nearest frame boundary (snap-down for
 *     start so we never lose user audio; snap-up for end same reason).
 *   - Slices the blob between those byte offsets. No decoding, no buffer
 *     allocation beyond the source ArrayBuffer.
 *   - Fades (multiTrimWithFades) are the one place where audio is decoded:
 *     the frames under a ramp are decoded, ramped sample by sample and
 *     encoded again; every other frame of the output is still the source's
 *     own bytes. See "Fades".
 *
 * Caveats:
 *   - (Fixed 2026-09-29.) Bit reservoir: a Layer-3 frame reads most of its
 *     audio from the tail of the frames before it, so after a byte splice
 *     the first frames read someone else's bytes (20–110 ms of noise at each
 *     cut in a song) and the head of a cut that starts mid-file had no
 *     reservoir at all (up to 78 ms muted). assembleMp3() now carries the
 *     right bytes across every splice and makes the first frame
 *     self-contained — see "Bit reservoir across a splice". What stays is
 *     one granule (~13 ms) where the decoder overlaps two blocks that were
 *     not neighbours; that is how any compressed-domain cut sounds, and it
 *     is smoother than a hard cut of the decoded samples. At 320 kbps with
 *     every frame full the head of a mid-file cut cannot be made
 *     self-contained: decoders mute its first granule (~13 ms).
 *   - (Fixed 2026-08-30.) The source's Xing/Info frame is dropped — it is
 *     frame 0 and holds the SOURCE's length — and a freshly computed one is
 *     written in its place by assembleMp3(). Before that fix the output
 *     carried no VBR header at all and every player guessed the duration
 *     from the first frame's bitrate; on a VBR source that guess is wrong
 *     (measured: 4:43 of audio announced as 5:32). See the block comment on
 *     buildXingHeaderFrame.
 *
 * Memory: O(file_size) — currently reads the whole blob into a Uint8Array.
 * For an 11 MB MP3 that's 11 MB. Phase A target was the 211 MB AudioBuffer
 * problem; this is 20× better. Future phase: streaming parser for very
 * large files.
 *
 * Usage:
 *     const r = await window.Mp3FrameTrim.trim(blob, startSec, endSec);
 *     // r.blob — trimmed MP3
 *     // r.actualStart, r.actualEnd — snapped boundaries (informational)
 *     // r.framesCopied — number of frames in output
 */
(function () {
    'use strict';

    // MPEG version: bits 19-20 of header. 00=MPEG2.5, 01=reserved, 10=MPEG2, 11=MPEG1
    var MPEG_VERSIONS = ['2.5', null, '2', '1'];
    // Layer: bits 17-18. 00=reserved, 01=Layer3, 10=Layer2, 11=Layer1
    var LAYERS = [null, 'Layer3', 'Layer2', 'Layer1'];

    // Bitrate tables in kbps. Index = bitrate index from header (4 bits).
    // 0 and 15 are reserved/bad.
    var BITRATE = {
        '1_Layer1':   [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448, -1],
        '1_Layer2':   [0, 32, 48, 56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, 384, -1],
        '1_Layer3':   [0, 32, 40, 48,  56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, -1],
        '2_Layer1':   [0, 32, 48, 56,  64,  80,  96, 112, 128, 144, 160, 176, 192, 224, 256, -1],
        '2_Layer2':   [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
        '2_Layer3':   [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
        '2.5_Layer1': [0, 32, 48, 56,  64,  80,  96, 112, 128, 144, 160, 176, 192, 224, 256, -1],
        '2.5_Layer2': [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
        '2.5_Layer3': [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
    };

    // Reusable all-zero TOC for the header-length probe in assembleMp3().
    var ZERO_TOC = new Uint8Array(100);

    // Sample rates in Hz. Index = sample rate index (2 bits). Index 3 is reserved.
    var SAMPLE_RATE = {
        '1':   [44100, 48000, 32000, -1],
        '2':   [22050, 24000, 16000, -1],
        '2.5': [11025, 12000,  8000, -1],
    };

    // Samples per frame depend on version + layer.
    var SAMPLES_PER_FRAME = {
        '1_Layer1': 384,  '1_Layer2': 1152, '1_Layer3': 1152,
        '2_Layer1': 384,  '2_Layer2': 1152, '2_Layer3': 576,
        '2.5_Layer1': 384,'2.5_Layer2': 1152,'2.5_Layer3': 576,
    };

    /**
     * Parse a single MPEG audio frame header.
     * @returns {object|null} {version, layer, bitrate, sampleRate, samples, frameLength, duration} or null
     */
    function parseFrameHeader(bytes, offset) {
        if (offset + 4 > bytes.length) return null;
        var b0 = bytes[offset], b1 = bytes[offset + 1], b2 = bytes[offset + 2];
        // 11-bit sync: byte0 == 0xFF, top 3 bits of byte1 == 1
        if (b0 !== 0xFF || (b1 & 0xE0) !== 0xE0) return null;

        var verBits = (b1 >> 3) & 0x03;
        var layerBits = (b1 >> 1) & 0x03;
        var version = MPEG_VERSIONS[verBits];
        var layer = LAYERS[layerBits];
        if (!version || !layer) return null;

        var key = version + '_' + layer;
        var brIdx = (b2 >> 4) & 0x0F;
        var srIdx = (b2 >> 2) & 0x03;
        var padding = (b2 >> 1) & 0x01;

        var bitrateTable = BITRATE[key];
        var sampleRateTable = SAMPLE_RATE[version];
        if (!bitrateTable || !sampleRateTable) return null;

        var bitrate = bitrateTable[brIdx];
        var sampleRate = sampleRateTable[srIdx];
        if (!bitrate || bitrate <= 0 || !sampleRate || sampleRate <= 0) return null;

        var samples = SAMPLES_PER_FRAME[key];
        var frameLength;
        if (layer === 'Layer1') {
            // Layer 1: floor((12 * bitrate * 1000 / sampleRate + padding) * 4)
            frameLength = (Math.floor(12 * bitrate * 1000 / sampleRate) + padding) * 4;
        } else {
            // Layer 2/3: floor(samples_per_frame / 8 * bitrate * 1000 / sampleRate) + padding.
            // For MPEG-1 Layer 2/3: 1152/8 = 144. For MPEG-2/2.5 Layer 3: 576/8 = 72.
            // For MPEG-2/2.5 Layer 2: 1152/8 = 144.
            var coef = samples / 8;
            frameLength = Math.floor(coef * bitrate * 1000 / sampleRate) + padding;
        }
        if (frameLength < 4) return null;

        return {
            version: version,
            layer: layer,
            bitrate: bitrate,
            sampleRate: sampleRate,
            samples: samples,
            frameLength: frameLength,
            duration: samples / sampleRate,
            // Raw header fields — needed to synthesise a Xing header frame that
            // parses as part of THIS stream (same version / rate / channels).
            brIdx: brIdx,
            srIdx: srIdx,
            channelMode: (bytes[offset + 3] >> 6) & 0x03,   // 3 = mono
            hb1: b1, hb2: b2, hb3: bytes[offset + 3],
        };
    }

    /**
     * Read main_data_begin from a Layer-3 frame's side info. This is the
     * bit-reservoir back-pointer: 0 means the frame is fully self-contained
     * (its main data starts right after its own side info); >0 means the
     * frame's audio data begins N bytes BACK inside previous frames' unused
     * space. A spliced stream is only glitch-free at a boundary if the first
     * kept frame has main_data_begin === 0 — otherwise the decoder reads
     * whatever bytes now precede it (our re-encoded fade chunk) as main
     * data → ~one granule of garbage AT FULL AMPLITUDE (comment #884,
     * Андрей 2026-06-30: «помеха (скачок), который очень хорошо слышен»).
     *
     * Layout: 4-byte header, optional 2-byte CRC (protection_bit === 0 →
     * CRC present), then side info. MPEG-1: main_data_begin = first 9 bits.
     * MPEG-2/2.5 (LSF): first 8 bits.
     *
     * Returns the integer value, or -1 if unreadable (caller treats -1 as
     * "not safe").
     */
    function frameMainDataBegin(bytes, offset) {
        var hdr = parseFrameHeader(bytes, offset);
        if (!hdr || hdr.layer !== 'Layer3') return -1;
        var crcPresent = (bytes[offset + 1] & 0x01) === 0;
        var s = offset + 4 + (crcPresent ? 2 : 0);
        if (s + 2 > bytes.length) return -1;
        if (hdr.version === '1') {   // MPEG_VERSIONS strings: '1'|'2'|'2.5'
            return ((bytes[s] << 1) | (bytes[s + 1] >> 7)) & 0x1FF;  // 9 bits
        }
        return bytes[s];  // LSF: 8 bits
    }

    /**
     * Detect ID3v2 header at start of file. Returns total bytes to skip
     * (header + tag) or 0 if no ID3v2.
     */
    function parseId3v2Length(bytes) {
        if (bytes.length < 10) return 0;
        // 'I'=0x49, 'D'=0x44, '3'=0x33
        if (bytes[0] !== 0x49 || bytes[1] !== 0x44 || bytes[2] !== 0x33) return 0;
        // Syncsafe size: 4 bytes, top bit of each is 0, take low 7 bits.
        var s = ((bytes[6] & 0x7F) << 21) | ((bytes[7] & 0x7F) << 14)
              | ((bytes[8] & 0x7F) << 7)  |  (bytes[9] & 0x7F);
        return 10 + s;
    }

    /** Detect ID3v1 tag (128 B 'TAG...' at end). */
    var APETAGEX = [0x41,0x50,0x45,0x54,0x41,0x47,0x45,0x58];  // 'APETAGEX'

    function isAsciiAt(bytes, offset, sig) {
        if (offset < 0 || offset + sig.length > bytes.length) return false;
        for (var i = 0; i < sig.length; i++) if (bytes[offset + i] !== sig[i]) return false;
        return true;
    }

    function readUint32LE(bytes, o) {
        return (bytes[o] | (bytes[o+1] << 8) | (bytes[o+2] << 16) | (bytes[o+3] << 24)) >>> 0;
    }

    function isId3v1At(bytes, offset) {
        return offset + 3 <= bytes.length
            && bytes[offset] === 0x54     // 'T'
            && bytes[offset + 1] === 0x41 // 'A'
            && bytes[offset + 2] === 0x47;// 'G'
    }

    /**
     * Walk MP3 frames from `start` to `end`. Returns array of frame index
     * descriptors: {offset, frameLength, cumStart} where cumStart is the
     * cumulative time in seconds before this frame.
     *
     * Resyncs (skips garbage bytes) when a frame header is malformed —
     * tolerates random ID3 frames between audio frames, MPEG version
     * transitions etc.
     */
    function indexFrames(bytes, start, end) {
        // Sync version preserved for callers that hold the main thread on
        // small inputs (tests / known-tiny files). The async variant below
        // is what production callers use — bug #443 (Артём, 2026-05-17):
        // on a multi-hundred-MB MP3 the synchronous loop runs ~10–60 s on a
        // mid-tier Android, freezing the UI between updateProgress(0.20)
        // and updateProgress(1.0). With no yield, the spinner sits at 20 %
        // and the user gives up. Telemetry: 414 sessions / 5 d abandon at
        // `last_stage:'wasm_ready'` (right after FFmpeg loads, just as the
        // multiTrim() chain enters indexFrames).
        var frames = [];
        var cumTime = 0;
        var pos = start;
        var resyncCount = 0;
        while (pos + 4 <= end) {
            var hdr = parseFrameHeader(bytes, pos);
            if (!hdr) {
                // Resync: scan for next 0xFF byte.
                var resync = pos + 1;
                while (resync < end && bytes[resync] !== 0xFF) resync++;
                if (resync >= end) break;
                pos = resync;
                resyncCount++;
                if (resyncCount > 1024) break; // garbage stream — bail
                continue;
            }
            // Validate next header consistency — Xing/Info "frame 0" lookalike,
            // free-format frames, etc., can fool a single header parse. Require
            // the next header to also parse OK (or hit EOF).
            if (frames.length === 0 && pos + hdr.frameLength + 4 <= end) {
                var next = parseFrameHeader(bytes, pos + hdr.frameLength);
                if (!next) {
                    // First "frame" looked OK but next slot is garbage —
                    // probably misaligned. Resync.
                    var resync2 = pos + 1;
                    while (resync2 < end && bytes[resync2] !== 0xFF) resync2++;
                    if (resync2 >= end) break;
                    pos = resync2;
                    resyncCount++;
                    continue;
                }
            }
            frames.push({ offset: pos, frameLength: hdr.frameLength, cumStart: cumTime });
            cumTime += hdr.duration;
            pos += hdr.frameLength;
        }
        return { frames: frames, totalDuration: cumTime, endByte: pos };
    }

    /**
     * Async variant of indexFrames — yields to the event loop every YIELD_BYTES
     * (~2 MB) so the main thread can paint progress and stay responsive on
     * 100 MB+ MP3s. On every yield, calls onProgress(bytesRead/totalBytes)
     * so the caller can update a spinner / progress bar.
     *
     * Yield cost: setTimeout(0) costs ~1-4 ms on Chrome/Android. At 2 MB
     * stride, a 200 MB MP3 yields ~100 times → ~100-400 ms total overhead
     * vs the 30+ s baseline. Imperceptible relative to the win.
     *
     * Pure JS, no Web Worker — Worker would help further but adds a
     * structured-clone copy of the 200 MB ArrayBuffer (Transferable can
     * dodge that but adds API complexity). Yield-based async is the
     * cheapest etalon that closes the freeze.
     */
    async function indexFramesAsync(bytes, start, end, onProgress) {
        var YIELD_BYTES = 2 * 1024 * 1024;  // yield every ~2 MB of input
        var frames = [];
        var cumTime = 0;
        var pos = start;
        var resyncCount = 0;
        var lastYieldPos = pos;
        var totalBytes = end - start;
        while (pos + 4 <= end) {
            // Yield to the event loop periodically. Use Promise resolution
            // (microtask) when no progress callback is provided — cheaper
            // than setTimeout. Use setTimeout(0) when progress is reported,
            // so the browser actually paints between yields.
            if (pos - lastYieldPos >= YIELD_BYTES) {
                lastYieldPos = pos;
                if (typeof onProgress === 'function') {
                    try { onProgress(Math.min(1, (pos - start) / totalBytes)); } catch (_) {}
                    await new Promise(function (r) { setTimeout(r, 0); });
                } else {
                    await Promise.resolve();
                }
            }
            var hdr = parseFrameHeader(bytes, pos);
            if (!hdr) {
                var resync = pos + 1;
                while (resync < end && bytes[resync] !== 0xFF) resync++;
                if (resync >= end) break;
                pos = resync;
                resyncCount++;
                if (resyncCount > 1024) break;
                continue;
            }
            if (frames.length === 0 && pos + hdr.frameLength + 4 <= end) {
                var next = parseFrameHeader(bytes, pos + hdr.frameLength);
                if (!next) {
                    var resync2 = pos + 1;
                    while (resync2 < end && bytes[resync2] !== 0xFF) resync2++;
                    if (resync2 >= end) break;
                    pos = resync2;
                    resyncCount++;
                    continue;
                }
            }
            frames.push({ offset: pos, frameLength: hdr.frameLength, cumStart: cumTime });
            cumTime += hdr.duration;
            pos += hdr.frameLength;
        }
        return { frames: frames, totalDuration: cumTime, endByte: pos };
    }

    /* ──────────────── Is this an MPEG audio stream at all ───────────────
     *
     * The ONE place on the site that decides it — /trim, /ringtone,
     * /audio-looper and /car-usb-music all ask here. Two bytes cannot answer
     * it, and two bytes were the answer in three places:
     *
     *   'ID3' at byte 0      → any container may carry an ID3 tag in front:
     *                          FLAC, raw AAC (radio rips do it routinely);
     *   0xFF + 3 set bits    → that is ALSO the ADTS sync word. Every raw
     *                          .aac file (0xFFF1 / 0xFFF9) passed as MP3.
     *
     * Measured 2026-09-23 on real music (MUSDB18 mixes): of 24 non-MP3 files
     * that passed that sniff (raw AAC with and without ID3, FLAC and AAC
     * behind an ID3 tag), 9 came out of a cut-from-the-start as the WHOLE
     * source file behind an MP3 header — 7 600+ decode errors, "2.5 s long".
     * On the same day 19 of 39 non-MP3 files passed /car-usb-music's
     * two-headers check. A sync word is eleven set bits; megabytes of
     * compressed data contain a pair of lookalikes at the right distance
     * almost always.
     *
     * A stream is what looks like a stream on all three counts:
     *   run      — MIN_RUN consecutive frames of ONE stream (same version,
     *              layer, sample rate, mono-ness). False runs inside non-MP3
     *              die by the third frame; 6 is twice that.
     *   coverage — frames of that stream fill ≥ 90 % of the bytes from its
     *              start to the end of the audio (real files: 0.995–1.000;
     *              false finds: ≤ 0.055).
     *   share    — the stream is at least half of the payload (a false run in
     *              the tail of a foreign file has share 0.000).
     * A run that hits the end of the data counts only when it starts where the
     * data starts — that is a very short file, not a find in a foreign tail.
     *
     * The costs are asymmetric and the thresholds lean on it: a real MP3 that
     * fails goes to the decode/re-encode lane (slower, a generation of loss);
     * a non-MP3 that passes goes to the person as a file nothing can play. */
    var STREAM_MIN_RUN = 6;
    var STREAM_MIN_COVERAGE = 0.9;
    var STREAM_MIN_SHARE = 0.5;

    function sameStream(a, b) {
        return a.version === b.version && a.layer === b.layer && a.sampleRate === b.sampleRate
            && ((a.channelMode === 3) === (b.channelMode === 3));
    }

    /** First frame followed by STREAM_MIN_RUN − 1 frames of the same stream. */
    function findStream(bytes, start, end) {
        var pos = start, guard = 0;
        while (pos + 4 <= end && guard++ < 1000000) {
            var hdr = parseFrameHeader(bytes, pos);
            if (hdr && hdr.frameLength > 4) {
                var run = 1, at = pos + hdr.frameLength, ok = true;
                while (run < STREAM_MIN_RUN) {
                    if (at + 4 > end) { ok = (pos === start); break; }
                    var next = parseFrameHeader(bytes, at);
                    if (!next || !sameStream(next, hdr)) { ok = false; break; }
                    run++;
                    at += next.frameLength;
                }
                if (ok) return { offset: pos, hdr: hdr };
            }
            pos++;
            while (pos < end && bytes[pos] !== 0xFF) pos++;
        }
        return null;
    }

    /**
     * Judge [start, end) given frames already indexed over it (the async walk
     * in indexBlob, or a sync walk here). Returns the stream's first frame,
     * the frames that belong to it, and its coverage — or null.
     */
    /* Bytes at the end that are no part of any stream: a run of zeros (or of
       0xFF) after the last frame. A file preallocated to a round size and
       filled only partly looks like this — the most common MP3 sizes on /trim
       in October 2026 were exactly 1 MiB and 512 KiB, and those files were
       declined here as «not an MPEG audio stream» (zeros counted against the
       coverage) and re-encoded. Only a run of at least PAD_MIN counts: a
       quiet last frame may end in a few zero bytes of its own. */
    var PAD_MIN = 1024;
    function trailingPad(bytes, start, end) {
        var p = end;
        var v = end > start ? bytes[end - 1] : -1;
        if (v !== 0x00 && v !== 0xFF) return 0;
        while (p > start && bytes[p - 1] === v) p--;
        return (end - p) >= PAD_MIN ? end - p : 0;
    }

    function judgeStream(bytes, start, end, frames) {
        end -= trailingPad(bytes, start, end);
        var first = findStream(bytes, start, end);
        if (!first) return null;
        var span = end - first.offset;
        if (span < STREAM_MIN_SHARE * (end - start)) return null;
        var own = [], covered = 0;
        for (var i = 0; i < frames.length; i++) {
            var f = frames[i];
            if (f.offset < first.offset) continue;
            var h = parseFrameHeader(bytes, f.offset);
            if (!h || !sameStream(h, first.hdr)) continue;
            own.push(f);
            covered += f.frameLength;
        }
        var coverage = span > 0 ? Math.min(1, covered / span) : 0;
        if (!own.length || coverage < STREAM_MIN_COVERAGE) return null;
        return { first: first, frames: own, coverage: coverage };
    }

    /** Synchronous verdict over [start, end) — small inputs and windows. */
    function locateStream(bytes, start, end) {
        var first = findStream(bytes, start, end);
        if (!first) return null;
        return judgeStream(bytes, start, end, indexFrames(bytes, first.offset, end).frames);
    }

    /** Does an MPEG stream begin in this window (bytes right after the tag)? */
    function looksLikeMpegStream(win) {
        return !!(win && win.length >= 128 && locateStream(win, 0, win.length));
    }

    /**
     * Is this file an MPEG audio stream? Reads the ID3v2 length and a 64 KB
     * window right behind the tag — never the whole file, so a 70 MB .aac is
     * turned away in milliseconds instead of being read into memory first.
     *
     * `opts.slug` — when the first bytes NOMINATE mp3 (an ID3 tag, a sync
     * word) and the stream does not confirm it, one `mp3_sniff_declined`
     * event says so, with the four bytes the payload really starts with. Until
     * 2026-09-23 this class was invisible: those files either produced a
     * silent "no frames" fall-through or a file nothing could play.
     */
    async function sniffMpeg(blob, opts) {
        if (!blob || typeof blob.slice !== 'function') return false;
        try {
            var head = new Uint8Array(await blob.slice(0, 10).arrayBuffer());
            var at = parseId3v2Length(head);
            if ((head[5] & 0x10) && at) at += 10;       // ID3v2.4 footer
            var win = new Uint8Array(await blob.slice(at, at + 65536).arrayBuffer());
            var ok = looksLikeMpegStream(win);
            var nominated = at > 0 || (head[0] === 0xFF && (head[1] & 0xE0) === 0xE0);
            if (!ok && nominated && opts && opts.slug && window.__ae) {
                var magic = '';
                for (var i = 0; i < 4 && i < win.length; i++) {
                    var c = win[i];
                    magic += (c >= 0x20 && c < 0x7F) ? String.fromCharCode(c) : ('\\x' + (c < 16 ? '0' : '') + c.toString(16));
                }
                try {
                    window.__ae('feature_use', {
                        slug: opts.slug, f: 'mp3_sniff_declined',
                        id3: at > 0 ? 1 : 0, head: magic, mb: Math.round((blob.size || 0) / 1048576),
                    });
                } catch (_) {}
            }
            return ok;
        } catch (e) {
            return false;
        }
    }

    /** Detect Xing/Info VBR header inside the first frame. Used to skip it
     *  from the OUTPUT when we cut the start, so the output stream begins
     *  with real audio data. The header itself stays inside the first frame
     *  data area; if our cut keeps the first frame, no special handling
     *  needed. If our cut starts later, the Xing header is naturally dropped.
     *
     *  Returns true if frame at offset contains a Xing/Info marker.
     */
    function isXingFrame(bytes, frameOffset, frameLength) {
        // Xing/Info marker can be at variable offsets inside the frame, after
        // the side info block. Cheapest robust check: scan first 200 bytes
        // of the frame for 'Xing' or 'Info' ASCII.
        var scanEnd = Math.min(frameOffset + frameLength, frameOffset + 200, bytes.length);
        for (var i = frameOffset + 4; i < scanEnd - 3; i++) {
            // 'X'=0x58 'i'=0x69 'n'=0x6E 'g'=0x67 OR 'I'=0x49 'n'=0x6E 'f'=0x66 'o'=0x6F
            if ((bytes[i] === 0x58 && bytes[i+1] === 0x69 && bytes[i+2] === 0x6E && bytes[i+3] === 0x67) ||
                (bytes[i] === 0x49 && bytes[i+1] === 0x6E && bytes[i+2] === 0x66 && bytes[i+3] === 0x6F)) {
                return true;
            }
        }
        /* VBRI — the Fraunhofer encoder's header, at a FIXED offset of 32 bytes
           after the 4-byte frame header (never after the side info, which is why
           the scan above cannot find it). Missing it cost twice over: the frame
           was counted as audio (duration off by one frame per copy) and it was
           spliced into the output, where it is not a header any more, only a
           frame of noise. Measured on a corpus run: `repeat ×3` of a VBRI source
           carried three such frames and announced 60.108 s against 60.029 s of
           audio. Fraunhofer-encoded files are uncommon but real — some hardware
           recorders and older commercial encoders write them. */
        if (frameOffset + 40 <= bytes.length
            && bytes[frameOffset + 36] === 0x56   // 'V'
            && bytes[frameOffset + 37] === 0x42   // 'B'
            && bytes[frameOffset + 38] === 0x52   // 'R'
            && bytes[frameOffset + 39] === 0x49) {// 'I'
            return true;
        }
        return false;
    }

    /* ── Xing/Info VBR header — the file must DECLARE its own length ──────
       Bug-report Nik29051980Maria 2026-08-29 (Android 10 / Yandex Browser,
       VBR mp3 from our own /song-generator): trimmed 5:00 → 4:43 and got a
       file every player showed as 5:32, with half a minute of "пустота" at
       the end that no amount of re-trimming removed.

       Root cause: this module dropped the source's Xing frame (it is frame 0,
       excluded from time accounting) and wrote nothing in its place. A VBR
       stream with no header carries its duration nowhere, so players
       extrapolate it as streamBytes × 8 / firstFrameBitrate. Her file's first
       audio frame is 160 kbps against a 187.6 kbps average, so the estimate
       overshot by 17 %: 6 638 501 × 8 / 160 000 = 331.9 s = 5:32 — her number
       to the second.

       MEASURED end-to-end on that exact file, real Chromium `audio.duration`
       (not ffprobe — a player is the instrument that matters here):
           source                      5:00   (true 5:00)
           trimmed, no header          5:36   (true 4:43)   ← what shipped
           trimmed, header written     4:43   (true 4:43)
           ffmpeg -c copy reference    4:43   (true 4:43)
       The header frame adds no audio: decoded output is sample-identical to
       the headerless build (lag 0, correlation 1.000 over the first 3 s).

       The docstring at the top of this file used to call this an accepted
       caveat — "modern players re-scan duration from the frame stream".
       Nothing does: not Chrome, not ffmpeg, not Android's media scanner. A
       scan costs a full file read, so they estimate instead, and the estimate
       was a guess we forced them to make.

       Two workarounds lived here only because of that guess —
       multiTrimWithFades refused VBR sources outright and the fade encoder
       pinned its bitrate to the source's first frame, both to stop the
       estimate drifting. With the truth declared neither is needed, and both
       are gone: fades take VBR, and our frames are encoded above the bitrate
       of the frames they replace (see "Fades").

       We always write 'Xing' rather than switching to 'Info' for constant
       bitrate: every decoder parses the two identically, FRAMES/BYTES are
       authoritative either way, and the alternative is a whole-stream bitrate
       scan whose only product is a cosmetic tag name. TOC formula validated
       against LAME's own TOC inside a Suno-encoded source: max deviation
       1/256, i.e. rounding. */

    /** Side-info block length in bytes — the Xing marker sits right after it. */
    function sideInfoLength(hdr) {
        var v1 = hdr.version === '1';
        return v1 ? (hdr.channelMode === 3 ? 17 : 32) : (hdr.channelMode === 3 ? 9 : 17);
    }

    /**
     * Synthesise the Xing header frame for a stream.
     *
     * @param {object} tpl  parsed header of the stream's FIRST audio frame.
     *        Version, sample rate, channel mode and emphasis are inherited, so
     *        the synthetic frame parses as an ordinary member of the stream.
     * @param {number} frameCount  audio frames, EXCLUDING this header frame.
     * @param {number} streamBytes MPEG bytes, INCLUDING this header frame
     *        (ID3v2/ID3v1 are not part of the stream).
     * @param {Uint8Array|null} toc  100 seek entries, or null for no TOC.
     * @returns {Uint8Array|null} null when no legal frame can hold the tag —
     *          caller then emits the stream unchanged (old behaviour).
     */
    function buildXingHeaderFrame(tpl, frameCount, streamBytes, toc) {
        // Layer 3 only. Layer 1/2 have no Xing convention and no VBR problem;
        // synthesising a frame there would be inventing a format.
        if (!tpl || tpl.layer !== 'Layer3') return null;
        var table = BITRATE[tpl.version + '_' + tpl.layer];
        if (!table) return null;
        var coef = tpl.samples / 8;
        var si = sideInfoLength(tpl);
        var need = 4 + si + 4 /*tag*/ + 4 /*flags*/ + 4 /*frames*/ + 4 /*bytes*/
            + (toc ? 100 : 0);
        var lenFor = function (kbps) { return Math.floor(coef * kbps * 1000 / tpl.sampleRate); };

        // Prefer the conventional 128 kbps header frame (what LAME and ffmpeg
        // write); step up only where the tag will not fit at this sample rate.
        var brIdx = -1;
        var preferred = [128, 160, 192, 256, 320];
        for (var pi = 0; pi < preferred.length && brIdx < 0; pi++) {
            var k = table.indexOf(preferred[pi]);
            if (k > 0 && lenFor(table[k]) >= need) brIdx = k;
        }
        for (var i = 1; i < 15 && brIdx < 0; i++) {
            if (table[i] > 0 && lenFor(table[i]) >= need) brIdx = i;
        }
        if (brIdx < 0) return null;

        var frame = new Uint8Array(lenFor(table[brIdx]));   // padding bit 0
        frame[0] = 0xFF;
        frame[1] = tpl.hb1 | 0x01;          // protection_bit 1 → no CRC to compute
        frame[2] = (brIdx << 4) | (tpl.srIdx << 2) | (tpl.hb2 & 0x01);
        frame[3] = tpl.hb3;

        var p = 4 + si;
        var TAG = 'Xing';
        for (var t = 0; t < 4; t++) frame[p + t] = TAG.charCodeAt(t);
        p += 4;
        var put32 = function (v) {
            frame[p] = (v >>> 24) & 0xFF; frame[p + 1] = (v >>> 16) & 0xFF;
            frame[p + 2] = (v >>> 8) & 0xFF; frame[p + 3] = v & 0xFF;
            p += 4;
        };
        put32(toc ? 0x07 : 0x03);           // FRAMES | BYTES | (TOC)
        put32(frameCount);
        put32(streamBytes);
        if (toc) frame.set(toc, p);
        return frame;
    }

    /**
     * One contiguous run of SOURCE frames [a, b) as an output segment.
     * `offsets` are frame starts relative to the segment, so the assembler can
     * build an exact TOC without re-reading a byte.
     */
    function srcSegment(idx, a, b) {
        var startByte = idx.frames[a].offset;
        var endByte = (b < idx.frames.length) ? idx.frames[b].offset : idx.fileEnd;
        var offsets = new Int32Array(b - a);
        for (var i = a; i < b; i++) offsets[i - a] = idx.frames[i].offset - startByte;
        return {
            blob: idx.blob.slice(startByte, endByte),
            frames: b - a,
            bytes: endByte - startByte,
            offsets: offsets,
            hdr: parseFrameHeader(idx.bytes, startByte),
            // Where these frames came from — what repairSplices() needs to
            // hand the frame after a splice the reservoir it was encoded against.
            src: idx, a: a, b: b,
            data: idx.bytes.subarray(startByte, endByte),
        };
    }

    /* ── Bit reservoir across a splice ────────────────────────────────────
       A Layer-3 frame's audio does not sit in the frame. Every frame's bytes
       after its header and side info (its "region") join one continuous
       stream, and a frame's audio starts `main_data_begin` bytes BEFORE its
       own region — in the tail of the frames ahead of it. LAME uses that on
       almost every frame (a 64-frame scan of LAME output found no frame with
       main_data_begin 0).

       Splice two runs of frames by bytes and the frame after the splice reads
       the tail of the frame that now precedes it — someone else's audio — as
       its own. The decoder does not fail; it decodes those bytes. MEASURED
       with ffmpeg on the multiTrim output, error against the source's own
       samples, 5-ms windows around the splice:
           tone, 320 kbps          ~20 ms at 1–1.5× the local level
           music, 192 / 128 kbps   35–45 ms, peak −13…−15 dBFS
           music, VBR q2           ~110 ms, up to 2.6× the local level
       — a click or a burst of noise at every cut in the middle of a song, and
       at every seam of a repeat.

       The repair: the frame BEFORE the splice ends with exactly the bytes the
       frame after it expects. Those bytes are free there — the frames that
       used them in the source were cut away — so its own audio is untouched.
       When its free tail is too short, the frame is written at a higher
       bitrate: a bigger frame with the same side info and the same 1152
       samples. No sample changes, nothing is re-encoded, and the Xing header
       counts the new sizes.

       Prod, first day (2026-09-29): 8 of 43 splices and 4 of 32 heads stayed
       unrepaired — high-bitrate files, where the frame before is full of its
       own data even at 320 kbps. Two more steps, measured on 320 kbps / 48 kHz
       music (12 → 18 splices of 20): the first frames after the splice are laid
       out anew with only the room there is (relay()), and failing that the
       splice moves to the neighbouring pair of frames, ≤ 3 on either side.
       What stays: a stream filled to the brim at 320 kbps whose reservoir level
       differs on the two sides over that whole window — ~31 ms of partly wrong
       audio instead of ~19, no click (HF burst ×1.1–1.2, same as a clean
       splice); the stat says so (failBr, failSr). A silent bridge frame would
       fix it at the price of 24 ms of silence inside the music — worse. */

    // `n` ≤ 24 bits, MSB first, from an absolute bit position.
    function readBitsN(bytes, bitPos, n) {
        var v = 0;
        for (var i = 0; i < n; i++) {
            var p = bitPos + i;
            v = (v << 1) | ((bytes[p >> 3] >> (7 - (p & 7))) & 1);
        }
        return v;
    }

    /**
     * Where a Layer-3 frame's main data lives: `mdb` bytes back from its
     * region, `ownUsed` bytes into its own region; the rest of the region is
     * free — only frames after it could read it. null for non-Layer-3.
     */
    function l3Geometry(bytes, off) {
        var hdr = parseFrameHeader(bytes, off);
        if (!hdr || hdr.layer !== 'Layer3') return null;
        var crc = (bytes[off + 1] & 0x01) === 0 ? 2 : 0;
        var head = 4 + crc + sideInfoLength(hdr);
        if (off + head > bytes.length) return null;
        var s = (off + 4 + crc) * 8;
        var nch = hdr.channelMode === 3 ? 1 : 2;
        var mdb, bits = 0, i;
        if (hdr.version === '1') {
            // main_data_begin 9, private 5|3, scfsi 4 per channel; then
            // 2 granules × channels blocks of 59 bits led by part2_3_length.
            mdb = readBitsN(bytes, s, 9);
            var base = s + (nch === 1 ? 18 : 20);
            for (i = 0; i < 2 * nch; i++) bits += readBitsN(bytes, base + i * 59, 12);
        } else {
            // LSF: main_data_begin 8, private 1|2; one granule, 63-bit blocks.
            mdb = readBitsN(bytes, s, 8);
            var baseL = s + (nch === 1 ? 9 : 10);
            for (i = 0; i < nch; i++) bits += readBitsN(bytes, baseL + i * 63, 12);
        }
        var dataBytes = (bits + 7) >> 3;
        return {
            hdr: hdr, crc: crc, head: head, mdb: mdb, dataBytes: dataBytes,
            regionLen: hdr.frameLength - head,
            ownUsed: Math.max(0, dataBytes - mdb),
        };
    }

    /**
     * The `need` bytes of the source's main-data stream that end where frame
     * `n`'s region begins — what frame n was encoded to find in the
     * reservoir. null when the source itself does not have them (a frame
     * that points before the first audio frame).
     */
    function reservoirBefore(src, n, need) {
        var out = new Uint8Array(need), w = need;
        for (var i = n - 1; w > 0 && i >= src.firstAudioFrame; i--) {
            var g = l3Geometry(src.bytes, src.frames[i].offset);
            if (!g) return null;
            var rEnd = src.frames[i].offset + g.head + g.regionLen;
            var take = Math.min(w, g.regionLen);
            out.set(src.bytes.subarray(rEnd - take, rEnd), w - take);
            w -= take;
        }
        return w === 0 ? out : null;
    }

    // CRC-16 of MPEG audio (poly 0x8005, init 0xFFFF, MSB first) over header
    // bytes 2–3 and the side info. Main data is not covered — a transplant
    // that keeps the header leaves a stored CRC valid; a new bitrate does not.
    function mpegCrc16(bytes, from, to, crc) {
        for (var i = from; i < to; i++) {
            crc ^= bytes[i] << 8;
            for (var k = 0; k < 8; k++) {
                crc = (crc & 0x8000) ? ((crc << 1) ^ 0x8005) & 0xFFFF : (crc << 1) & 0xFFFF;
            }
        }
        return crc;
    }

    /**
     * Frame `fb` rebuilt around its own header and side info: its region
     * starts with `lead` (the part of its own main data that lives there) and
     * ENDS with `tail` (the reservoir of the frame after it); `mdb` is written
     * as its main_data_begin. Re-headed at the smallest bitrate whose frame
     * holds both — same side info, same samples; null when even the largest
     * does not.
     */
    function rebuildFrame(fb, mdb, lead, tail) {
        var g = l3Geometry(fb, 0);
        if (!g) return null;
        var need = lead.length + tail.length, h = g.hdr;
        var len = -1, brIdx = h.brIdx, pad = (fb[2] >> 1) & 1;
        if (g.regionLen >= need) {
            len = h.frameLength;
        } else {
            var table = BITRATE[h.version + '_' + h.layer], coef = h.samples / 8;
            for (var b = h.brIdx; b < 15 && len < 0; b++) {
                for (var p = 0; p <= 1 && len < 0; p++) {
                    var fl = Math.floor(coef * table[b] * 1000 / h.sampleRate) + p;
                    if (fl > h.frameLength && fl - g.head >= need) {
                        len = fl; brIdx = b; pad = p;
                    }
                }
            }
            if (len < 0) return null;
        }
        var out = new Uint8Array(len);
        out.set(fb.subarray(0, g.head));
        out[2] = (brIdx << 4) | (fb[2] & 0x0D) | (pad << 1);
        var s = 4 + g.crc;
        if (h.version === '1') {
            out[s] = (mdb >> 1) & 0xFF;
            out[s + 1] = (out[s + 1] & 0x7F) | ((mdb & 1) << 7);
        } else {
            out[s] = mdb & 0xFF;
        }
        out.set(lead, g.head);
        out.set(tail, len - tail.length);
        if (g.crc && (len !== h.frameLength || mdb !== g.mdb)) {
            var c = mpegCrc16(out, 6, g.head, mpegCrc16(out, 2, 4, 0xFFFF));
            out[4] = c >> 8; out[5] = c & 0xFF;
        }
        return out;
    }

    /** The last frame before a splice: own data untouched, next frame's reservoir at the end. */
    function carryReservoir(fb, res) {
        var g = l3Geometry(fb, 0);
        return g ? rebuildFrame(fb, g.mdb, fb.subarray(g.head, g.head + g.ownUsed), res) : null;
    }

    /** One rebuilt frame as a segment — it has no source run to splice against. */
    function frameSegment(bytes) {
        return {
            blob: new Blob([bytes], { type: 'audio/mpeg' }),
            frames: 1,
            bytes: bytes.length,
            offsets: new Int32Array(1),
            hdr: parseFrameHeader(bytes, 0),
        };
    }

    /** `seg` without its first `k` frames, still spliceable against its source. */
    function dropHead(seg, k) {
        var cut = seg.offsets[k], offsets = new Int32Array(seg.frames - k);
        for (var i = k; i < seg.frames; i++) offsets[i - k] = seg.offsets[i] - cut;
        return {
            blob: seg.blob.slice(cut),
            frames: seg.frames - k,
            bytes: seg.bytes - cut,
            offsets: offsets,
            hdr: parseFrameHeader(seg.data, cut),
            src: seg.src, a: seg.a + k, b: seg.b,
            data: seg.data.subarray(cut),
            orig: seg.orig || seg,
        };
    }

    // `seg` without its last `k` frames, still spliceable against its source.
    function dropTail(seg, k) {
        var n = seg.frames - k, cut = seg.offsets[n];
        return {
            blob: seg.blob.slice(0, cut),
            frames: n,
            bytes: cut,
            offsets: seg.offsets.subarray(0, n),
            hdr: seg.hdr,
            src: seg.src, a: seg.a, b: seg.b - k,
            data: seg.data.subarray(0, cut),
            orig: seg.orig || seg,
        };
    }

    /* Repair one splice between `seg` and `next` in place, or say it cannot:
       carry the reservoir in the last frame; else re-lay the first frames after
       the splice. Returns the replacement segments for [seg, next] or null. */
    function repairPair(seg, next, stat) {
        var need = frameMainDataBegin(next.data, 0);
        var lastOff = seg.offsets[seg.frames - 1];
        var lastBytes = seg.data.subarray(lastOff);
        var headPart = seg.frames > 1 ? [{
            blob: seg.blob.slice(0, lastOff), frames: seg.frames - 1, bytes: lastOff,
            offsets: seg.offsets.subarray(0, seg.frames - 1), hdr: seg.hdr,
        }] : [];
        if (need === 0) return [seg, next];
        var res = reservoirBefore(next.src, next.a, need);
        var patched = res ? carryReservoir(lastBytes, res) : null;
        if (patched) {
            if (patched.length !== lastBytes.length) stat.grown++;
            return headPart.concat([frameSegment(patched), next]);
        }
        var gA = l3Geometry(lastBytes, 0);
        var r = gA ? relay(next, Math.min(mdbMax(gA.hdr), maxRegion(gA) - gA.ownUsed)) : null;
        patched = r ? carryReservoir(lastBytes, r.head) : null;
        if (!patched) return null;
        stat.relayed = (stat.relayed || 0) + 1;
        if (patched.length !== lastBytes.length) stat.grown++;
        var repl = r.frames.map(frameSegment);
        if (r.next < next.frames) repl.push(dropHead(next, r.next));
        return headPart.concat([frameSegment(patched)], repl);
    }

    // main_data_begin is 9 bits in MPEG-1, 8 in MPEG-2/2.5 (LSF).
    function mdbMax(h) { return h.version === '1' ? 511 : 255; }

    // The largest region this frame can have: the top bitrate of its version, with padding.
    function maxRegion(g) {
        var h = g.hdr, table = BITRATE[h.version + '_' + h.layer];
        return Math.floor((h.samples / 8) * table[14] * 1000 / h.sampleRate) + 1 - g.head;
    }

    // Frame k of a segment, as bytes (its own side info — gain edits live there).
    function frameAt(seg, k) {
        return seg.data.subarray(seg.offsets[k], k + 1 < seg.frames ? seg.offsets[k + 1] : seg.bytes);
    }

    // A frame's whole main data, in stream order: the reservoir part, then the
    // head of its own region. The bits are the source's; only where they sit changes.
    function mainDataBlob(src, i, fb, g) {
        var md = new Uint8Array(g.dataBytes);
        if (g.mdb > 0) {
            var own = reservoirBefore(src, i, g.mdb);
            if (!own) return null;
            md.set(own.subarray(0, Math.min(g.mdb, g.dataBytes)));
        }
        if (g.dataBytes > g.mdb) md.set(fb.subarray(g.head, g.head + g.ownUsed), g.mdb);
        return md;
    }

    /* When the frame before a splice cannot carry the whole reservoir of the
       frame after it — its own data fills its region even at the top bitrate
       (320 kbps at 48 kHz: 7 splices in 20 measured, 2026-09-29), and at the
       start of the output there is no frame before at all — the first frames of
       the run are laid out anew: the first one takes from the frame before only
       the `room` it has (0 at the start), puts the rest of its data in its own,
       larger region, and ends it with the reservoir of the next frame. When even
       that does not fit, the next frame gets whatever room is left and the same
       step repeats, until a frame can hand its successor the original reservoir —
       from there on the run is its original bytes. The bits of the audio stay the
       source's: only main_data_begin and the frame sizes change. Returns the
       rebuilt frames, the bytes for the frame before (`head`) and where the
       original bytes resume, or null. */
    var RELAY_MAX = 8;
    function relay(seg, room) {
        var fb = frameAt(seg, 0), g = l3Geometry(fb, 0);
        if (!g) return null;
        var md = mainDataBlob(seg.src, seg.a, fb, g);
        if (!md) return null;
        var m = Math.max(0, Math.min(room, mdbMax(g.hdr), md.length));
        var head = md.subarray(0, m), out = [];
        for (var k = 0; k < seg.frames && k < RELAY_MAX; k++) {
            var rest = md.subarray(m);
            var nfb = k + 1 < seg.frames ? frameAt(seg, k + 1) : null;
            var ng = nfb ? l3Geometry(nfb, 0) : null;
            var tail = new Uint8Array(0);
            if (ng && ng.mdb > 0) {
                tail = reservoirBefore(seg.src, seg.a + k + 1, ng.mdb);
                if (!tail) return null;
            }
            var built = rebuildFrame(fb, m, rest, tail);
            if (built) { out.push(built); return { head: head, frames: out, next: k + 1 }; }
            if (!ng) return null;
            var room2 = maxRegion(g) - rest.length;
            if (room2 < 0) return null;
            var md2 = mainDataBlob(seg.src, seg.a + k + 1, nfb, ng);
            if (!md2) return null;
            var m2 = Math.min(room2, mdbMax(ng.hdr), md2.length);
            built = rebuildFrame(fb, m, rest, md2.subarray(0, m2));
            if (!built) return null;
            out.push(built);
            fb = nfb; g = ng; md = md2; m = m2;
        }
        return null;
    }

    /* The start of the output has the same problem with nothing before it:
       the frame ahead of the first one is our Xing header, which decoders
       skip, so the first frame's reservoir is simply missing. ffmpeg mutes
       the granules it cannot read. MEASURED on a cut that starts mid-file:
       one frame on 128–192 kbps music, up to three frames (78 ms) on 32–64
       kbps speech and MPEG-2 — the head of every such trim, and of every
       "part" of a long recording, was silence where the audio should be.
       Repair: the first frame carries all of its main data itself
       (main_data_begin 0) and ends with the reservoir of the frame after it —
       no reservoir needed from anywhere, no sample or duration changed. */
    function repairStart(segments, stat) {
        var seg = segments[0];
        if (!seg || !seg.src || !seg.data) return segments;
        var g = l3Geometry(seg.data, 0);
        if (!g || g.mdb === 0) return segments;
        stat.start = 'unrepaired';
        var r = relay(seg, 0);
        if (!r) return segments;
        var grown = false;
        for (var q = 0; q < r.frames.length; q++) {
            if (r.frames[q].length !== frameAt(seg, q).length) grown = true;
        }
        stat.start = grown ? 'grown' : 'repaired';
        if (r.frames.length > 1) stat.relayed = (stat.relayed || 0) + 1;
        return r.frames.map(frameSegment).concat(r.next < seg.frames ? [dropHead(seg, r.next)] : [], segments.slice(1));
    }

    /**
     * Every place where the next segment does not continue the previous one
     * in its source is a splice; the last frame before it is rebuilt to carry
     * the reservoir of the first frame after it. Segments without a source
     * and splices that cannot be repaired stay as they were — the
     * pre-repair output, never worse.
     */
    function repairSplices(segments) {
        var stat = { splices: 0, repaired: 0, grown: 0, start: 'none' };
        segments = repairStart(segments, stat);
        var out = [];
        for (var i = 0; i < segments.length; i++) {
            var seg = segments[i], next = segments[i + 1];
            if (!next || !seg.src || !next.src || !seg.data || !next.data
                || (seg.src === next.src && seg.b === next.a)) {
                out.push(seg);
                continue;
            }
            // Layer 1/2 have no reservoir: nothing reaches across the splice.
            if (frameMainDataBegin(next.data, 0) < 0) { out.push(seg); continue; }
            stat.splices++;
            var fixed = repairPair(seg, next, stat), dA = 0, dB = 0;
            /* A stream that fills every frame (CBR 320 at 48 kHz: 7 splices in 20)
               has no room anywhere for the reservoir this splice needs. The pair of
               frames one to three away usually has it — the free tail after one
               frame is the next one's main_data_begin, and those vary. The cut moves
               by those frames (24–72 ms; the cut is frame-snapped anyway); the
               regions the caller reports move with it (seg.orig.shift*). */
            for (var total = 1; !fixed && total <= 4; total++) {
                for (dA = 0; dA <= Math.min(total, 3) && !fixed; dA++) {
                    dB = total - dA;
                    if (dB > 3 || dA >= seg.frames || dB >= next.frames) continue;
                    var a2 = dA ? dropTail(seg, dA) : seg, b2 = dB ? dropHead(next, dB) : next;
                    fixed = repairPair(a2, b2, stat);
                    if (fixed) {
                        var oA = seg.orig || seg, oB = next.orig || next;
                        oA.shiftTail = (oA.shiftTail || 0) + dA;
                        oB.shiftHead = (oB.shiftHead || 0) + dB;
                        stat.shifted = (stat.shifted || 0) + dA + dB;
                    }
                }
            }
            if (!fixed) {
                if (!stat.failBr) {
                    var gF = l3Geometry(seg.data, seg.offsets[seg.frames - 1]);
                    if (gF) { stat.failBr = gF.hdr.bitrate; stat.failSr = gF.hdr.sampleRate; }
                }
                out.push(seg);
                continue;
            }
            stat.repaired++;
            // All but the last replacement are final; the last one continues the
            // next run and still meets the splice after it.
            for (var q = 0; q < fixed.length - 1; q++) out.push(fixed[q]);
            segments[i + 1] = fixed[fixed.length - 1];
        }
        return { segments: out, stat: stat };
    }

    /**
     * Final assembly for EVERY producer in this module: ordered audio segments
     * in, one Blob out — source ID3v2 first, then a freshly computed Xing
     * header, then the audio, then the source ID3v1.
     *
     * A single funnel on purpose. The defect this closes was one forgotten
     * header duplicated across four separate `parts.push` sites; a fifth site
     * would have forgotten it again.
     */
    function assembleMp3(idx, segments) {
        var repair = repairSplices(segments);
        segments = repair.segments;
        var frameCount = 0, audioBytes = 0, tpl = null;
        for (var s = 0; s < segments.length; s++) {
            frameCount += segments[s].frames;
            audioBytes += segments[s].bytes;
            if (!tpl && segments[s].hdr) tpl = segments[s].hdr;
        }
        var parts = [];
        if (idx.id3v2End > 0) parts.push(idx.blob.slice(0, idx.id3v2End));

        var header = null;
        if (tpl && frameCount > 0) {
            // The header frame's own length is needed BEFORE the TOC, because
            // TOC offsets are measured from the start of the stream — which is
            // this frame. Probe once with a tag of identical shape.
            var probe = buildXingHeaderFrame(tpl, frameCount, 0, ZERO_TOC);
            if (probe) {
                var streamBytes = probe.length + audioBytes;
                var cum = new Int32Array(frameCount);
                var acc = probe.length, fi = 0;
                for (var g = 0; g < segments.length; g++) {
                    var seg = segments[g], ofs = seg.offsets;
                    for (var k = 0; k < seg.frames; k++) {
                        // A segment with no frame table (none produced today)
                        // falls back to even spacing, error bounded by it.
                        cum[fi++] = acc + (ofs ? ofs[k] : Math.round(k * seg.bytes / seg.frames));
                    }
                    acc += seg.bytes;
                }
                var toc = new Uint8Array(100);
                for (var e = 0; e < 100; e++) {
                    var at = Math.min(frameCount - 1, Math.floor(e * frameCount / 100));
                    var v = Math.floor(256 * cum[at] / streamBytes);
                    toc[e] = v > 255 ? 255 : v;
                }
                /* Entry 0 is the start of the STREAM, not the first audio frame.
                   Measured against LAME on 1 / 3 / 10 / 40 / 300 s files: it
                   writes 0 every time, including where the header frame is
                   3.97/256 of the whole stream. Ours pointed one header frame in,
                   which rounds to 0 on any normal file but reached 122 on a
                   single-frame cut — a reader interpolating from there starts the
                   whole seek curve late. */
                toc[0] = 0;
                header = buildXingHeaderFrame(tpl, frameCount, streamBytes, toc);
                if (header) parts.push(header);
            }
        }
        for (var q = 0; q < segments.length; q++) parts.push(segments[q].blob);
        /* Trailing tags, in the order they appear in a file: ID3v1 then APEv2.
           The old code sliced ID3v1 at `bytes.length - 128`, which is inside the
           APE tag whenever both are present — right for the common case, silently
           wrong for the file that has both. */
        if (idx.hasId3v1 && idx.id3v1Start >= 0) {
            parts.push(idx.blob.slice(idx.id3v1Start, idx.id3v1Start + 128));
        }
        if (idx.apeStart >= 0) parts.push(idx.blob.slice(idx.apeStart));
        return {
            blob: new Blob(parts, { type: 'audio/mpeg' }),
            headerWritten: !!header,
            frameCount: frameCount,
            splices: repair.stat,
        };
    }

    /**
     * Internal: index a blob into frames + tags, prepare for byte slicing.
     * Shared by single-region trim and multi-region trim/cut.
     *
     * `opts.onProgress(ratio, stage)` is called repeatedly during the
     * three sub-phases — `'read'` (0–10 %, ArrayBuffer fetch), `'index'`
     * (10–95 %, async frame walk) and `'xing'` (95–100 %, Xing header
     * adjustment). The async indexFrames yields to the event loop every
     * ~2 MB so the page can actually paint progress instead of freezing.
     * Bug #443 (Артём, 2026-05-17): pre-fix this whole function blocked
     * the main thread for 10–60 s on a 100 MB+ MP3, spinner stuck at the
     * caller's static updateProgress(0.20), user gave up.
     */
    async function indexBlob(blob, opts) {
        opts = opts || {};
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
        function emit(ratio, stage) {
            if (onProgress) { try { onProgress(ratio, stage); } catch (_) {} }
        }
        emit(0, 'read');
        var ab = await blob.arrayBuffer();
        var bytes = new Uint8Array(ab);
        emit(0.10, 'index');
        var id3v2End = parseId3v2Length(bytes);
        var fileEnd = bytes.length;
        /* APEv2 sits AFTER the audio, like ID3v1, and foobar2000 and friends
           keep real metadata (including ReplayGain) there. Two reasons to take
           it off the end before walking frames: the tag bytes were inside the
           frame region, so a cut that ran to the end spliced them into the audio
           stream and into the byte count of the header we now write; and the tag
           itself was dropped from every output. Layout: optional 32-byte header,
           items, 32-byte footer — the footer's size field covers footer+items. */
        var apeStart = -1;
        if (fileEnd >= 32 && isAsciiAt(bytes, fileEnd - 32, APETAGEX)) {
            var apeSize = readUint32LE(bytes, fileEnd - 32 + 12);
            if (apeSize > 0 && apeSize <= fileEnd) {
                apeStart = fileEnd - apeSize;
                if (apeStart >= 32 && isAsciiAt(bytes, apeStart - 32, APETAGEX)) apeStart -= 32;
                fileEnd = apeStart;
            }
        }
        var hasId3v1 = false;
        var id3v1Start = -1;
        if (fileEnd >= 128 && isId3v1At(bytes, fileEnd - 128)) {
            hasId3v1 = true;
            fileEnd -= 128;
            id3v1Start = fileEnd;
        }
        /* Zeros (or 0xFF) after the last frame are the file's preallocated
           tail, not audio and not a tag: off the end before the walk, so they
           neither count against the stream verdict nor ride into the output
           (a cut to the end of a 1 MiB file carried 570 KB of zeros). */
        var padBytes = trailingPad(bytes, id3v2End, fileEnd);
        if (padBytes) fileEnd -= padBytes;
        var idx = await indexFramesAsync(bytes, id3v2End, fileEnd, function (subRatio) {
            // index sub-phase consumes 10 → 95 % of overall progress.
            emit(0.10 + subRatio * 0.85, 'index');
        });
        var frames = idx.frames;
        if (frames.length === 0) {
            throw new Error('mp3-frame-trim: no valid MP3 frames found');
        }
        /* The walk finds "frames" in anything — a run of lookalikes inside AAC
           or FLAC data is enough. Before any cut, ask whether this IS a
           stream: a cut of a non-MPEG file from the start used to come out as
           the whole source behind an MP3 header. The throw sends every caller
           (trim, multiTrim, fades, repeat) to its decode lane. */
        if (!judgeStream(bytes, id3v2End, fileEnd, frames)) {
            throw new Error('mp3-frame-trim: not an MPEG audio stream');
        }
        // Detect Xing/Info header in frame 0; exclude from time accounting.
        emit(0.95, 'xing');
        var firstAudioFrame = 0;
        if (frames.length > 1 && isXingFrame(bytes, frames[0].offset, frames[0].frameLength)) {
            firstAudioFrame = 1;
            var t = 0;
            for (var i = 1; i < frames.length; i++) {
                frames[i].cumStart = t;
                var hdrI = parseFrameHeader(bytes, frames[i].offset);
                if (hdrI) t += hdrI.duration;
            }
        }
        var lastFrame = frames[frames.length - 1];
        var lastDur = (parseFrameHeader(bytes, lastFrame.offset) || { duration: 0 }).duration;
        var totalAudioDuration = lastFrame.cumStart + lastDur;
        emit(1.0, 'done');
        return {
            blob: blob, bytes: bytes,
            id3v2End: id3v2End, fileEnd: fileEnd, hasId3v1: hasId3v1, padBytes: padBytes,
            id3v1Start: id3v1Start, apeStart: apeStart,
            frames: frames, firstAudioFrame: firstAudioFrame,
            totalAudioDuration: totalAudioDuration,
        };
    }

    /**
     * Pick frame index range covering [startSec, endSec] with snap-down
     * start / snap-up end (don't lose user audio at boundaries).
     */
    function frameRangeFor(idx, startSec, endSec) {
        var frames = idx.frames;
        if (startSec >= idx.totalAudioDuration) {
            throw new Error('mp3-frame-trim: start beyond audio duration');
        }
        var startIdx = idx.firstAudioFrame;
        for (var i = idx.firstAudioFrame; i < frames.length; i++) {
            if (frames[i].cumStart > startSec) break;
            startIdx = i;
        }
        var endIdx = frames.length;
        for (var j = startIdx + 1; j < frames.length; j++) {
            if (frames[j].cumStart >= endSec) { endIdx = j; break; }
        }
        if (endIdx <= startIdx) {
            throw new Error('mp3-frame-trim: no frames in selected range');
        }
        return { startIdx: startIdx, endIdx: endIdx };
    }

    /**
     * Lossless MP3 trim — single keep region.
     */
    async function mp3FrameTrim(blob, startSec, endSec) {
        if (!blob || typeof blob.arrayBuffer !== 'function') {
            throw new Error('mp3-frame-trim: blob required');
        }
        if (typeof startSec !== 'number' || typeof endSec !== 'number'
            || startSec < 0 || endSec <= startSec) {
            throw new Error('mp3-frame-trim: invalid time range');
        }

        var idx = await indexBlob(blob);  // single-trim: no progress wiring yet
        var rng = frameRangeFor(idx, startSec, endSec);
        var frames = idx.frames;

        var startByte = frames[rng.startIdx].offset;
        var endByte = (rng.endIdx < frames.length)
            ? frames[rng.endIdx].offset
            : idx.fileEnd;

        var actualStart = frames[rng.startIdx].cumStart;
        var actualEnd = (rng.endIdx < frames.length)
            ? frames[rng.endIdx].cumStart
            : idx.totalAudioDuration;

        var built = assembleMp3(idx, [srcSegment(idx, rng.startIdx, rng.endIdx)]);

        return {
            blob: built.blob,
            actualStart: actualStart,
            actualEnd: actualEnd,
            framesCopied: rng.endIdx - rng.startIdx,
            totalDuration: idx.totalAudioDuration,
            headerWritten: built.headerWritten,
        };
    }

    /**
     * Lossless MP3 multi-region cut — keep N regions, drop everything else.
     *
     * Replaces the WASM `reencodeAndConcat` path used by trim's "Вырезать"
     * (cut-region apply) UX. That path decoded → re-encoded each segment
     * through FFmpeg WASM at 150+ MB peak heap and double-encoded the audio
     * (quality loss). This path slices the original frame stream byte-level:
     * O(file_size) memory, bit-identical to source, no quality loss, fast.
     *
     * @param {Blob} blob - input MP3
     * @param {Array<{start:number,end:number}>} keepRegions - segments to keep,
     *        sorted ascending, non-overlapping. Each [start..end) in seconds.
     * @returns {Promise<{blob:Blob, regionsCopied:number, framesCopied:number,
     *                    actualRegions: Array, totalDuration:number}>}
     */
    async function mp3FrameMultiTrim(blob, keepRegions, opts) {
        opts = opts || {};
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
        if (!blob || typeof blob.arrayBuffer !== 'function') {
            throw new Error('mp3-frame-trim: blob required');
        }
        if (!Array.isArray(keepRegions) || keepRegions.length === 0) {
            throw new Error('mp3-frame-trim: keepRegions array required');
        }
        // Validate ordering + non-overlap. We REQUIRE these — fixing them
        // here would mask caller bugs, and the trim blade already merges
        // overlapping cut regions before computing keep segments.
        for (var k = 0; k < keepRegions.length; k++) {
            var r = keepRegions[k];
            if (typeof r.start !== 'number' || typeof r.end !== 'number'
                || r.start < 0 || r.end <= r.start) {
                throw new Error('mp3-frame-trim: invalid region at index ' + k);
            }
            if (k > 0 && r.start < keepRegions[k - 1].end) {
                throw new Error('mp3-frame-trim: regions overlap or unsorted at index ' + k);
            }
        }

        // Bug #443 (Артём, 2026-05-17) fix: forward the caller's onProgress
        // through indexBlob so it can paint progress during the long async
        // frame walk. indexBlob itself consumes 0–95 % (the bulk of the
        // work is bytes traversal); the splice + Blob construction below
        // consumes 95–100 % and is essentially instant (zero-copy slices).
        var idx = await indexBlob(blob, {
            onProgress: onProgress ? function (r, stage) {
                onProgress(r * 0.95, stage || 'index');
            } : null,
        });

        // For each region, compute frame range. Catch + skip out-of-range
        // regions cleanly (e.g. last region may extend slightly past
        // total duration due to rounding — clamp instead of reject).
        var perRegion = [];
        for (var k2 = 0; k2 < keepRegions.length; k2++) {
            var reg = keepRegions[k2];
            var clampedStart = Math.max(0, Math.min(reg.start, idx.totalAudioDuration));
            var clampedEnd = Math.max(clampedStart, Math.min(reg.end, idx.totalAudioDuration));
            if (clampedEnd <= clampedStart) continue; // empty after clamp
            try {
                var rng = frameRangeFor(idx, clampedStart, clampedEnd);
                perRegion.push({
                    request: reg,
                    startIdx: rng.startIdx,
                    endIdx: rng.endIdx,
                    actualStart: idx.frames[rng.startIdx].cumStart,
                    actualEnd: (rng.endIdx < idx.frames.length)
                        ? idx.frames[rng.endIdx].cumStart
                        : idx.totalAudioDuration,
                });
            } catch (e) {
                // Region completely out of range — skip rather than throw.
                continue;
            }
        }
        if (perRegion.length === 0) {
            throw new Error('mp3-frame-trim: all regions empty after clamp');
        }

        // Build output: ID3v2 header + frame slices for each region + ID3v1.
        // Blob.slice() returns zero-copy views; the resulting Blob is a
        // composition with no extra allocation beyond the source ArrayBuffer.
        // Splice phase — zero-copy Blob.slice() per region. Cheap (no
        // ArrayBuffer copy, browser handles it as a view); consumes the
        // remaining 95–100 % of progress in a single emit because the
        // total wall-clock for this loop is sub-second even for hundreds
        // of regions.
        if (onProgress) try { onProgress(0.97, 'splice'); } catch (_) {}
        var segs = [];
        var totalFrames = 0;
        var actualRegions = [];
        for (var p = 0; p < perRegion.length; p++) {
            var pr = perRegion[p];
            segs.push(srcSegment(idx, pr.startIdx, pr.endIdx));
            totalFrames += (pr.endIdx - pr.startIdx);
            actualRegions.push({
                start: pr.actualStart,
                end: pr.actualEnd,
                framesCopied: pr.endIdx - pr.startIdx,
            });
        }
        var builtMulti = assembleMp3(idx, segs);
        // A splice moved to the neighbouring pair of frames (repairSplices) moved
        // these regions' edges with it — report where they really are.
        for (var sp = 0; sp < segs.length; sp++) {
            var sh = segs[sp], fd = sh.hdr ? sh.hdr.duration : 0;
            if (!sh.shiftTail && !sh.shiftHead) continue;
            actualRegions[sp].start += (sh.shiftHead || 0) * fd;
            actualRegions[sp].end -= (sh.shiftTail || 0) * fd;
            actualRegions[sp].framesCopied -= (sh.shiftHead || 0) + (sh.shiftTail || 0);
            totalFrames -= (sh.shiftHead || 0) + (sh.shiftTail || 0);
        }
        if (onProgress) try { onProgress(1.0, 'done'); } catch (_) {}

        return {
            blob: builtMulti.blob,
            regionsCopied: perRegion.length,
            framesCopied: totalFrames,
            actualRegions: actualRegions,
            totalDuration: idx.totalAudioDuration,
            headerWritten: builtMulti.headerWritten,
            splices: builtMulti.splices,
        };
    }

    /* ── Fades ────────────────────────────────────────────────────────────
       A fade is a gain ramp on the SAMPLES. The frames under the ramp are
       decoded, ramped and encoded again; every other frame of the output is
       the source's own bytes.

       Two earlier designs lived here, and both are gone.

       · May–July 2026: the ramp was encoded and spliced in, but 529 samples
         late (the decoder's own delay was not counted) and with the
         reservoir of the first source frame after it cut away — a click at
         the seam (comment #884).
       · July–October 2026: no encode at all. The 8-bit `global_gain` of each
         granule was lowered instead, the mp3gain trick: no seam, so no click
         — and no ramp either. One unit of global_gain is 1.5 dB, so the
         "fade" was a staircase. MEASURED 2026-10-06 on a 320 kbps / 48 kHz
         song (support chat 592: «помехи по ходу трека с хрипами и провалами
         звука»): on a 10-s fade the level stood still for 0.8–1.5 s and then
         dropped 1.5 dB at once, 13 times before −20 dB, and each drop broke
         the MDCT's alias cancellation for a granule — a burst of distortion
         20 dB under the music. Against the true ramp the output was off by
         −27 dB (median) over the whole fade. This code on the same file:
         −51 dB, the ramp within ±0.2 dB, every frame outside it unchanged.

       What makes the seam between our frames and the source's silent. Each
       line was measured by breaking it, on that song:

         alignment    our encoder is fed so that ITS frame k holds exactly the
                      samples of source frame k. One sample off: −16 dB at
                      the seam instead of −33…−52; 529 off (the first design):
                      +3 dB, a double hit. The offset is not ours to know —
                      decoders differ in how much of their delay they hide —
                      so it is measured: fadeProbe().
         block types  the granule before a seam and the granule after it must
                      meet on a long window slope. A "start" block of the
                      source followed by a long block of ours: −10 dB for
                      12 ms. The seam moves frame by frame until the side info
                      on both sides agrees (95–97 % of frames do at once).
         reservoir    the first source frame after our last one reads up to
                      511 bytes back — out of frames that are no longer
                      there. They go to the end of our last frames, spread
                      over as many as it takes (fadeSeal). At the top bitrate
                      those frames are full, so the last two or three come
                      from an encode of the same samples one step lower.
         context      the encoder hears real audio on both sides of what is
                      kept: FADE_WARM frames before (dropped), FADE_LOOK after.

       Then the result is judged the way a listener meets it: the frames
       around each seam are decoded again and compared with the source
       (fadeVerify) — are our frames in their place, is the seam no worse
       than the frames beside it, do the source's frames still decode to
       their own samples. Anything off — a decoder that drops a frame, a
       browser with its own idea of delay — throws, and the caller re-encodes
       the whole file: slower and a generation worse, never wrong. */
    // Counted in MPEG-1 frames (1152 samples); MPEG-2/2.5 frames are half that and get twice as
    // many — all but FADE_CAND: a seam is looked for among frame BOUNDARIES, and a dozen of
    // them is a dozen at any frame size (twice as many at 8 kHz put the seam up to 1.7 s past
    // the ramp, and everything up to the seam is encoded again).
    var FADE_CAND = 12;          // frames next to the ramp searched for a seam (≈0.3 s at 44.1 kHz)
    var FADE_WARM = 3;           // real frames encoded ahead of a mid-stream chunk, then dropped
    var FADE_LOOK = 3;           // real frames encoded after the last kept one
    var FADE_PRE = 3;            // frames decoded ahead of the first sample that is used
    var FADE_TAIL = 8;           // frames encoded again, one bitrate step lower, to seal a seam (run-up included)
    var FADE_CHECK_BEFORE = 5;   // frames decoded again on each side of a seam
    var FADE_CHECK_AFTER = 4;
    var FADE_MERGE = 12;         // two ramps closer than this are one encode
    var FADE_SPREAD = 4;         // our last frames a reservoir may be spread over without encoding again
    // What a decoder still carries from the frames before: one granule of MDCT
    // overlap and 480 samples of synthesis filter. Past that many samples a
    // slot depends only on its own frames.
    var DECODER_MEMORY = 576 + 480;
    // Where a sample fed to the encoder lands inside a frame's slot of the decoded
    // file: LAME's 576 samples of delay plus the decoder's 529.
    var CODEC_DELAY = 1105;

    function fadeError(code, msg) {
        var e = new Error('mp3-frame-trim: ' + msg);
        e.code = code;
        return e;
    }

    /* Window type of every granule × channel of a Layer-3 frame: 0 long,
       1 start, 2 short, 3 stop, −1 mixed. A granule's LEFT slope is long for
       0 and 1, its RIGHT slope for 0 and 3; two granules cancel each other's
       aliasing only when the slopes that meet are of one kind. */
    function granuleBlocks(bytes, off) {
        var h = parseFrameHeader(bytes, off);
        if (!h || h.layer !== 'Layer3') return null;
        var crc = (bytes[off + 1] & 0x01) === 0 ? 2 : 0;
        var s = (off + 4 + crc) * 8, nch = h.channelMode === 3 ? 1 : 2, out = [], row, blk, c;
        if (h.version === '1') {
            // part2_3_length 12, big_values 9, global_gain 8, scalefac_compress 4,
            // window_switching_flag 1, block_type 2, mixed_block_flag 1.
            for (var gr = 0; gr < 2; gr++) {
                row = [];
                for (c = 0; c < nch; c++) {
                    blk = s + (nch === 1 ? 18 : 20) + (gr * nch + c) * 59;
                    row.push(!readBitsN(bytes, blk + 33, 1) ? 0
                        : (readBitsN(bytes, blk + 36, 1) ? -1 : readBitsN(bytes, blk + 34, 2)));
                }
                out.push(row);
            }
        } else {
            // LSF: one granule, scalefac_compress is 9 bits.
            row = [];
            for (c = 0; c < nch; c++) {
                blk = s + (nch === 1 ? 9 : 10) + c * 63;
                row.push(!readBitsN(bytes, blk + 38, 1) ? 0
                    : (readBitsN(bytes, blk + 41, 1) ? -1 : readBitsN(bytes, blk + 39, 2)));
            }
            out.push(row);
        }
        return out;
    }

    /* May the frame at (nextBytes, nextOff) follow the frame at (prevBytes,
       prevOff) when the two come from different encodes of the same samples? */
    function seamBlocksOk(prevBytes, prevOff, nextBytes, nextOff) {
        var a = granuleBlocks(prevBytes, prevOff), b = granuleBlocks(nextBytes, nextOff), i;
        if (!a || !b) return false;
        var right = a[a.length - 1], left = b[0];
        for (i = 0; i < right.length; i++) if (right[i] !== 0 && right[i] !== 3) return false;
        for (i = 0; i < left.length; i++) if (left[i] !== 0 && left[i] !== 1) return false;
        return true;
    }

    /* A frame of silence that ends with `res`. Placed ahead of a run of frames
       cut out of the middle of a stream it gives the run's first frame the
       reservoir it was encoded against, so a decoder reads the run exactly as
       it reads those frames in the file. */
    function silentDonor(bytes, off, res) {
        var h = parseFrameHeader(bytes, off);
        if (!h || h.layer !== 'Layer3') return null;
        var table = BITRATE[h.version + '_' + h.layer], coef = h.samples / 8, head = 4 + sideInfoLength(h);
        for (var b = 1; b < 15; b++) {
            var len = Math.floor(coef * table[b] * 1000 / h.sampleRate);
            if (len - head < res.length) continue;
            var out = new Uint8Array(len);          // side info all zero: no main data, silence
            out[0] = 0xFF;
            out[1] = h.hb1 | 0x01;                  // no CRC
            out[2] = (b << 4) | (h.hb2 & 0x0C);     // this bitrate, the stream's rate, no padding
            out[3] = h.hb3;
            out.set(res, len - res.length);
            return out;
        }
        return null;
    }

    /* Source frames [a, b) as a stream a decoder can start on; `lead` frames of
       ours (0 or 1) precede them. */
    function contextSlice(idx, a, b) {
        var from = idx.frames[a].offset;
        var to = b < idx.frames.length ? idx.frames[b].offset : idx.fileEnd;
        var need = frameMainDataBegin(idx.bytes, from), donor = null;
        if (need > 0) {
            var res = reservoirBefore(idx, a, need);
            if (res) donor = silentDonor(idx.bytes, from, res);
        }
        var out = new Uint8Array((donor ? donor.length : 0) + to - from);
        if (donor) out.set(donor, 0);
        out.set(idx.bytes.subarray(from, to), donor ? donor.length : 0);
        return { bytes: out, lead: donor ? 1 : 0 };
    }

    /* The browser's own decoder and the page's MP3 encoder. A caller (a test
       under node) may pass its own pair as opts.fadeCodec:
         decode(bytes, sampleRate, channels) → {channels: Float32Array[], sampleRate}
         encode(channels, sampleRate, kbps, slug) → Uint8Array of bare frames. */
    function webFadeCodec() {
        var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        var enc = window.MP3Encoder;
        if (!OAC || !enc || typeof enc.encode !== 'function') return null;
        return {
            id: 'web',
            decode: function (bytes, sampleRate, channels) {
                // Pinned to the stream's own rate: a context at the hardware rate would resample.
                var ctx = new OAC(channels, 1, sampleRate);
                var ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
                return new Promise(function (resolve, reject) {
                    var p = ctx.decodeAudioData(ab, resolve, reject);     // old Safari: callbacks only
                    if (p && typeof p.then === 'function') p.then(resolve, reject);
                }).then(function (buf) {
                    var ch = [];
                    for (var c = 0; c < buf.numberOfChannels; c++) ch.push(buf.getChannelData(c));
                    return { channels: ch, sampleRate: buf.sampleRate };
                });
            },
            encode: function (channels, sampleRate, kbps, slug) {
                var view = {
                    numberOfChannels: channels.length, length: channels[0].length,
                    sampleRate: sampleRate, duration: channels[0].length / sampleRate,
                    getChannelData: function (c) { return channels[c]; },
                    copyFromChannel: function (dst, c, from) {
                        from = from || 0;
                        dst.set(channels[c].subarray(from, from + dst.length));
                    },
                };
                // ownStream: these frames go back into the person's own stream — the plan's
                // quality ceiling is for exports and does not apply (__qualityTier.clamp).
                return enc.encode(view, { bitrate: kbps, slug: slug, bare: true, ownStream: true })
                    .then(function (blob) { return blob.arrayBuffer(); })
                    .then(function (ab) { return new Uint8Array(ab); });
            },
        };
    }

    /* What this encoder and this decoder do to a signal at one bitrate. Three
       things are not ours to assume, and each one wrong is heard at the seam:

         shift  decode(encode(y))[n] = y[n − shift]. LAME delays by 576 samples
                and a decoder by 529 — 1105 in all when the decoder hides none
                of it. Some hide their 529, some all of it, a new browser may do
                anything, and a ramp encoded one sample off is a click.
         level  LAME turns the input DOWN before it encodes at a constant
                bitrate: ×0.95 up to 160 kbps, ×0.97 at 192, ×0.98 at 224, ×1
                from 256 (its guard against clipping). Left alone, our frames
                are 0.45 dB under the source's and the seam is a step.
         kept   below a bitrate of its own choosing LAME lowers the SAMPLE RATE
                (128 kbps is the least that keeps stereo at 44.1 or 48 kHz).
                Frames at another rate cannot stand in the stream.

       So a noise burst (for the shift) and three tones (for the level) go
       through both, once per bitrate per page.

       ⚠️ The answer is remembered under the bitrate that was ASKED for, and it
       describes the bitrate the encoder GAVE (`kbps` of the result) — under a
       plan's ceiling the two differ. fadeEncode() checks its frames against
       it and asks again when the ceiling has moved (fadeProbesForget). */
    var _fadeProbes = {};
    function fadeProbesForget(codec) {
        var mine = (codec.id || '') + '|';
        for (var key in _fadeProbes) if (key.indexOf(mine) === 0) delete _fadeProbes[key];
    }
    function fadeProbe(codec, hdr, kbps) {
        var sr = hdr.sampleRate, nch = hdr.channelMode === 3 ? 1 : 2;
        var key = (codec.id || '') + '|' + sr + '|' + nch + '|' + kbps;
        if (!_fadeProbes[key]) {
            _fadeProbes[key] = (async function () {
                var U = 1152, y = new Float32Array(U * 12), seed = 20261006, i, s;
                var n0 = U * 3, n1 = U * 6, t0 = U * 7, t1 = U * 11;
                for (i = n0; i < n1; i++) {
                    seed = (Math.imul(seed, 1103515245) + 12345) & 0x7fffffff;
                    y[i] = (seed / 0x7fffffff - 0.5) * 0.5;
                }
                for (i = t0; i < t1; i++) {
                    var t = 2 * Math.PI * (i - t0) / sr, edge = Math.min(1, (i - t0) / 480, (t1 - i) / 480);
                    y[i] = edge * (0.2 * Math.sin(440 * t) + 0.15 * Math.sin(1000 * t) + 0.1 * Math.sin(2300 * t));
                }
                var bytes = await codec.encode(nch > 1 ? [y, y.slice()] : [y], sr, kbps, 'mp3-frame-trim');
                var frames = indexFrames(bytes, parseId3v2Length(bytes), bytes.length).frames;
                var h = frames.length ? parseFrameHeader(bytes, frames[0].offset) : null;
                if (!h) throw fadeError('FADE_CALIBRATION', 'fade: the encoder returned no frames');
                if (!sameStream(h, hdr)) return { kbps: h.bitrate, kept: false };
                var pcm = await codec.decode(bytes, sr, nch);
                var d = pcm && pcm.channels && pcm.channels[0];
                if (!d || pcm.sampleRate !== sr) throw fadeError('FADE_CALIBRATION', 'fade: the decoder returned no usable audio');
                var best = 0, bestV = -Infinity;
                for (s = -U * 2; s <= U * 3; s++) {
                    var v = 0;
                    for (i = n0; i < n1; i += 3) { var j = i + s; if (j >= 0 && j < d.length) v += y[i] * d[j]; }
                    if (v > bestV) { bestV = v; best = s; }
                }
                var ey = 0, ed = 0, cross = 0, q;
                for (i = n0; i < n1; i++) {
                    q = i + best;
                    var dv = (q >= 0 && q < d.length) ? d[q] : 0;
                    ey += y[i] * y[i]; ed += dv * dv; cross += y[i] * dv;
                }
                if (!(cross > 0) || cross / Math.sqrt(ey * ed) < 0.5) {
                    throw fadeError('FADE_CALIBRATION', 'fade: encoder and decoder do not line up');
                }
                var num = 0, den = 0;
                for (i = t0 + U; i < t1 - U; i++) {
                    q = i + best;
                    if (q < 0 || q >= d.length) continue;
                    num += y[i] * d[q]; den += y[i] * y[i];
                }
                var level = den > 0 ? num / den : 1;
                if (!(level > 0.5 && level < 2)) throw fadeError('FADE_CALIBRATION', 'fade: encoder level ' + level);
                return { kbps: h.bitrate, kept: true, shift: best, level: level };
            })();
            _fadeProbes[key].catch(function () { delete _fadeProbes[key]; });
        }
        return _fadeProbes[key];
    }

    /* The bitrate to ask the encoder for: `want`, or the first step of the table
       above it at which the encoder keeps the stream's sample rate — larger
       frames than the source's own, never a worse sound. */
    async function fadeBitrate(codec, hdr, want) {
        var probe = await fadeProbe(codec, hdr, want);
        if (probe.kept) return { ask: want, probe: probe };
        var table = BITRATE[hdr.version + '_Layer3'];
        for (var b = 1; b < 15; b++) {
            if (table[b] <= want) continue;
            probe = await fadeProbe(codec, hdr, table[b]);
            if (probe.kept) return { ask: table[b], probe: probe };
        }
        throw fadeError('FADE_NOT_ELIGIBLE', 'fade: the encoder does not keep ' + hdr.sampleRate + ' Hz');
    }

    /* Source frames [a, b) decoded. Sample j of frame f's slot, as THIS decoder
       counts samples, is ch[c][fadePos(dec, k, f) + j]. */
    async function fadeDecode(idx, codec, k, a, b) {
        var d0 = Math.max(idx.firstAudioFrame, a - k.pre), d1 = Math.min(idx.frames.length, b);
        var sl = contextSlice(idx, d0, d1);
        var pcm = await codec.decode(sl.bytes, k.sr, k.nch);
        if (!pcm || !pcm.channels || !pcm.channels[0] || pcm.sampleRate !== k.sr) {
            throw fadeError('FADE_DECODE', 'fade: source frames did not decode');
        }
        return { ch: pcm.channels, d0: d0, lead: sl.lead };
    }
    function fadePos(dec, k, f) { return k.spf * (dec.lead + f - dec.d0); }

    /* Encode source frames [plan.first, plan.first + plan.count) under the
       ramp. The encoder's frame e holds the samples of source frame
       plan.first − plan.warm + e: the first `warm` frames are there for the
       encoder's memory and are dropped by fadeFrame(). `gainAt(G)` is the gain
       of slot position G (frame × samples-per-frame + sample), 0 outside the
       region being kept. */
    async function fadeEncode(codec, k, dec, plan, kbps, gainAt, again) {
        var br = await fadeBitrate(codec, k.hdr, kbps), probe = br.probe;
        var spf = k.spf, len = spf * (plan.warm + plan.count + k.look);
        var base = fadePos(dec, k, plan.first - plan.warm) + probe.shift;
        var slot = spf * (plan.first - plan.warm) + CODEC_DELAY;
        var ch = [], peak = 0, c, m;
        for (c = 0; c < k.nch; c++) {
            var y = new Float32Array(len), p = dec.ch[c < dec.ch.length ? c : 0];
            for (m = 0; m < len; m++) {
                var at = m + base;
                if (at < 0 || at >= p.length) continue;
                var g = gainAt(slot + m);
                if (g <= 0) continue;
                var v = p[at] * g;
                y[m] = v;
                if (v > peak) peak = v; else if (-v > peak) peak = -v;
            }
            ch.push(y);
        }
        // Undo the encoder's own gain. The encoder takes 16-bit input, so a peak
        // this pushes past full scale is cut at it — and comes out exactly where
        // the encoder's gain would have left it anyway; every other sample comes
        // out at the source's level. (Holding the whole chunk back for the sake
        // of its one highest peak left music mastered to full scale 0.3 dB low
        // under the ramp, a step at the seam — measured.)
        var boost = Math.abs(probe.level - 1) > 0.004 ? 1 / probe.level : 1;
        if (boost !== 1) {
            for (c = 0; c < ch.length; c++) for (m = 0; m < len; m++) ch[c][m] *= boost;
        }
        var bytes = await codec.encode(ch, k.sr, br.ask, k.slug);
        var frames = indexFrames(bytes, parseId3v2Length(bytes), bytes.length).frames;
        if (frames.length && isXingFrame(bytes, frames[0].offset, frames[0].frameLength)) frames = frames.slice(1);
        if (frames.length < plan.warm + plan.count) {
            throw fadeError('FADE_ENCODE', 'fade: encoder returned ' + frames.length + ' frames, ' + (plan.warm + plan.count) + ' needed');
        }
        // The probe spoke for the bitrate the encoder gave THEN. A plan's ceiling
        // that has moved since — signed in, Premium bought or run out, with the
        // page still open — makes the same request come back at another bitrate,
        // and with it another gain of the encoder's own (0.45 dB between 128 and
        // 320): what was measured no longer describes these frames.
        var got = frames.length > plan.warm ? parseFrameHeader(bytes, frames[plan.warm].offset) : null;
        if (got && got.bitrate !== probe.kbps && sameStream(got, k.hdr)) {
            if (again) {
                throw fadeError('FADE_CALIBRATION', 'fade: the encoder answers ' + br.ask + ' kbps with ' + got.bitrate + ', measured ' + probe.kbps);
            }
            fadeProbesForget(codec);
            return fadeEncode(codec, k, dec, plan, kbps, gainAt, true);
        }
        for (var e = plan.warm; e < plan.warm + plan.count; e++) {
            var h = parseFrameHeader(bytes, frames[e].offset);
            if (!h || !sameStream(h, k.hdr)) throw fadeError('FADE_ENCODE', 'fade: encoder changed the stream');
            // Frames are taken out of this encode one by one — each must stand alone.
            if (frameMainDataBegin(bytes, frames[e].offset) !== 0) {
                throw fadeError('FADE_ENCODE', 'fade: encoder frames lean on a reservoir');
            }
        }
        return {
            bytes: bytes, frames: frames, plan: plan, shift: probe.shift,
            kbps: parseFrameHeader(bytes, frames[plan.warm].offset).bitrate,
            // The encoder's own gain at this bitrate (undone above), and how much of
            // the input the undoing pushed past full scale.
            level: probe.level, over: peak * boost > 1 ? peak * boost : 0,
        };
    }
    /* The plan that encodes only the run-up to frame `lastF` of `plan` — enough
       for the encoder to reach that frame in the state the full encode had. */
    function fadeTailPlan(k, plan, lastF) {
        var first = Math.max(plan.first, lastF - k.tail);
        return { first: first, count: lastF - first + 1, warm: first === plan.first ? plan.warm : k.warm };
    }
    function fadeFrameAt(enc, f) { return enc.frames[f - enc.plan.first + enc.plan.warm]; }

    function fadeFrame(enc, f) {
        var fr = fadeFrameAt(enc, f);
        return enc.bytes.subarray(fr.offset, fr.offset + fr.frameLength);
    }

    /* The last `n` frames of an encode — [lastF − n + 1, lastF] — laid out
       anew so that the last one ends with `res`, the bytes the source frame
       after it reads back. Each frame starts in the room the one before it
       leaves (main_data_begin > 0) — the bit reservoir used as it was designed;
       a frame that needs more room than it has is written at a larger frame
       size, same side info and samples. Returns the frames, or null when
       `res` does not fit even so. */
    function fadeSpread(enc, lastF, n, res) {
        var fb = [], g = [], md = [], begin = [0], i;
        for (i = 0; i < n; i++) {
            fb[i] = fadeFrame(enc, lastF - n + 1 + i);
            g[i] = l3Geometry(fb[i], 0);
            if (!g[i] || g[i].mdb !== 0) return null;
            md[i] = fb[i].subarray(g[i].head, g[i].head + g[i].dataBytes);
        }
        var top = maxRegion(g[0]), most = mdbMax(g[0].hdr);
        for (i = 0; i < n; i++) {
            var room = top - (md[i].length - begin[i]);        // free at the end of frame i's region
            if (i === n - 1) { if (room < res.length) return null; break; }
            begin[i + 1] = Math.max(0, Math.min(most, room, md[i + 1].length));
        }
        var out = [];
        for (i = 0; i < n; i++) {
            var tail = i === n - 1 ? res : md[i + 1].subarray(0, begin[i + 1]);
            var built = rebuildFrame(fb[i], begin[i], md[i].subarray(begin[i]), tail);
            if (!built) return null;
            out.push(built);
        }
        return out;
    }

    /* Our last frame is `lastF`; in the output it is followed by source frame
       `next`, which reads `need` bytes back, out of frames that are no longer
       there. They are put at the end of our last frames:
         · in the room those frames have or gain at a larger size — nothing is
           encoded again (fit, grown, spread);
         · at the top bitrate our frames are full and cannot grow. Then the last
           few are taken from an encode of the same samples ONE step lower
           (`reencode(kbps, lastF)`), and the reservoir is spread over them: at
           320 kbps that is two or three frames at 256 instead of a single frame
           at 160 — MEASURED at the seam on 320 kbps songs: −14…−20 dB with the
           one starved frame, −24…−30 dB spread.
       Returns {frames, from, how, kbps}: `frames` replace our frames from `from`
       on. null — no way found. */
    async function fadeSeal(idx, k, enc, lastF, next, reencode) {
        var need = frameMainDataBegin(idx.bytes, idx.frames[next].offset);
        var res = need > 0 ? reservoirBefore(idx, next, need) : null;
        // No reservoir to carry — or the source has no such bytes either.
        if (!res) return { frames: [], from: lastF + 1, how: 'none', kbps: enc.kbps };
        var first = fadeFrame(enc, lastF).length, n, out;
        for (n = 1; n <= FADE_SPREAD && lastF - n + 1 >= enc.plan.first; n++) {
            out = fadeSpread(enc, lastF, n, res);
            if (out) {
                return { frames: out, from: lastF - n + 1, kbps: enc.kbps,
                    how: n > 1 ? 'spread' : (out[0].length === first ? 'fit' : 'grown') };
            }
        }
        var table = BITRATE[k.hdr.version + '_Layer3'], tries = 0;
        for (var b = 14; b >= 1 && tries < 2; b--) {
            if (table[b] >= enc.kbps) continue;
            tries++;
            var low = await reencode(table[b], lastF);
            // Asked for less, got as much: a plan's ceiling, or the line below
            // which the encoder would change the sample rate.
            if (low.kbps >= enc.kbps) return null;
            for (n = 1; lastF - n + 1 >= low.plan.first; n++) {
                // Same samples, same windows — or these frames would not fit
                // between their neighbours.
                var f = lastF - n + 1;
                if (JSON.stringify(granuleBlocks(fadeFrame(low, f), 0)) !== JSON.stringify(granuleBlocks(fadeFrame(enc, f), 0))) break;
                out = fadeSpread(low, lastF, n, res);
                if (out) return { frames: out, from: f, how: 'lower', kbps: low.kbps };
            }
        }
        return null;
    }

    /* Our frames [from, to) as an output segment; `sealed.frames` replace those
       from `sealed.from` on. */
    function fadeSegment(enc, from, to, sealed) {
        var n = to - from, parts = [], offsets = new Int32Array(n), total = 0, i;
        for (i = 0; i < n; i++) {
            var fb = (sealed && from + i >= sealed.from) ? sealed.frames[from + i - sealed.from] : fadeFrame(enc, from + i);
            offsets[i] = total;
            parts.push(fb);
            total += fb.length;
        }
        var bytes = new Uint8Array(total);
        for (i = 0; i < n; i++) bytes.set(parts[i], offsets[i]);
        // No `src`: assembleMp3() must not look for a reservoir across these edges —
        // our frames stand alone, and the edge towards the source is sealed above.
        return {
            blob: new Blob([bytes], { type: 'audio/mpeg' }),
            frames: n, bytes: total, offsets: offsets, hdr: parseFrameHeader(bytes, 0), raw: bytes,
        };
    }

    function dbRatio(e, r) { return 10 * Math.log(Math.max(e, 1e-30) / Math.max(r, 1e-30)) / Math.LN10; }

    /* The frames around a seam, decoded as the output holds them, against what
       should be there: the source's own samples, times the ramp where the
       frames are ours.
         v.bytes        the frames [v.from, v.to) of the output, v.lead frames of ours ahead
         v.seam         the first frame after the seam
         v.oursFrom/To  which of those frames are encoded by us
         v.shift        the encoder-to-decoder shift those frames were made with
       Throws FADE_UNVERIFIED; returns what it measured. */
    async function fadeVerify(codec, k, dec, v) {
        var pcm = await codec.decode(v.bytes, k.sr, k.nch);
        var d = pcm && pcm.channels && pcm.channels[0];
        if (!d || pcm.sampleRate !== k.sr) throw fadeError('FADE_UNVERIFIED', 'fade: the seam did not decode');
        var p = dec.ch[0], spf = k.spf, gOff = CODEC_DELAY - v.shift;
        // Error and reference energy over the SLOTS of frames [q0, q1). A decoder
        // that hides `gOff` samples of its delay holds a frame's slot that much
        // earlier in what it returns, and returns that much less of the first
        // slot: what it did not return is not compared (`n` says how much was).
        function energies(q0, q1, lag) {
            var e = 0, r = 0, n = 0;
            for (var q = q0; q < q1; q++) {
                var ours = q >= v.oursFrom && q < v.oursTo;
                var pa = fadePos(dec, k, q) - gOff, da = spf * (v.lead + q - v.from) - gOff + lag, ga = spf * q;
                for (var j = 0; j < spf; j++) {
                    if (da + j < 0 || da + j >= d.length || pa + j < 0 || pa + j >= p.length) continue;
                    var w = p[pa + j];
                    if (ours) w *= v.gainAt(ga + j);
                    var x = d[da + j] - w;
                    e += x * x; r += w * w; n++;
                }
            }
            return { e: e, r: r, n: n, floor: 1e-9 * n };
        }
        // A decoder starts cold, and what it carries over from the frames before
        // lasts DECODER_MEMORY samples: the first slots of this stream are not what
        // the file plays there, and neither is the source's first slot after ours.
        var settle = Math.ceil(DECODER_MEMORY / spf);
        var warm = v.from + settle + 1, out = { oursDb: null, seamDb: null, sourceDb: null };

        // 1. Our frames sit exactly where the source's did. What is judged here is
        //    the PLACE, not the codec: under a plan's ceiling our frames are encoded
        //    well below the source (128 kbps against 320), and on noise-like music —
        //    applause, cymbals — that alone is an error of −4 dB against the source's
        //    samples (MEASURED). So the question is asked the way it can be answered
        //    at any bitrate: does another place fit better than ours? One or two
        //    samples away — a decoder that counts differently; 529, 576, 1105 or a
        //    frame away — one that hides its delay in one stream and not in another,
        //    or drops a frame. A neighbouring sample must fit clearly better (by
        //    1.5 dB) to count, a far place twice as well (3 dB): that is a miss
        //    louder than the codec's own noise — a smaller one is not heard over it,
        //    and two stretches of a steady tone differ by less than that by chance.
        var o0 = Math.max(warm, v.oursFrom), o1 = Math.min(v.to, v.oursTo);
        var lossy = 0;          // error / signal of our frames: what the encoder itself loses
        if (o1 > o0) {
            var z = energies(o0, o1, 0);
            if (z.n < spf) throw fadeError('FADE_UNVERIFIED', 'fade: the decoder returned too little of the seam');
            if (z.r > z.floor) {
                out.oursDb = dbRatio(z.e, z.r);
                lossy = z.e / z.r;
                var lags = [-2, -1, 1, 2, 529, -529, 576, -576, CODEC_DELAY, -CODEC_DELAY, spf, -spf];
                for (var li = 0; li < lags.length; li++) {
                    var y = energies(o0, o1, lags[li]);
                    var better = Math.abs(lags[li]) <= 2 ? 0.7 : 0.5;
                    if (y.n >= spf && y.r > y.floor && y.e / y.r < better * lossy) {
                        throw fadeError('FADE_UNVERIFIED', 'fade: encoded frames are ' + lags[li] + ' samples off');
                    }
                }
                // Further from the source than silence would be.
                if (lossy > 1) throw fadeError('FADE_UNVERIFIED', 'fade: encoded frames do not carry the source');
            }
        }
        // 2. The two slots that meet at the seam: one of them is a frame of ours, so
        //    the seam is as clean as our frames are and no cleaner — −8 dB when the
        //    encode is good, up to 3 dB over our frames' own error when a ceiling
        //    makes it coarse. What a seam must never be is noise: a reservoir read
        //    from the wrong bytes decodes at full scale, 0 dB and up.
        var s0 = Math.max(warm, v.seam - 1), s1 = Math.min(v.to, v.seam + 1);
        if (s1 > s0) {
            var m = energies(s0, s1, 0);
            if (m.n >= spf && m.r > m.floor) {
                out.seamDb = dbRatio(m.e, m.r);
                if (m.e > Math.max(0.16, Math.min(1, 2 * lossy)) * m.r) {
                    throw fadeError('FADE_UNVERIFIED', 'fade: seam is ' + out.seamDb.toFixed(1) + ' dB');
                }
            }
        }
        // 3. The source's frames next to ours still decode to the source's samples:
        //    past the slots that still hear our last frame, before our first.
        var x0 = v.oursFrom <= v.from ? v.seam + settle : warm;
        var x1 = v.oursFrom <= v.from ? v.to : v.seam;
        if (x1 > x0) {
            var t = energies(x0, x1, 0);
            if (t.n >= spf && t.r > t.floor) {
                out.sourceDb = dbRatio(t.e, t.r);
                if (t.e > 1e-4 * t.r) throw fadeError('FADE_UNVERIFIED', 'fade: source frames by the seam are ' + out.sourceDb.toFixed(1) + ' dB');
            }
        }
        return out;
    }

    /**
     * Multi-region cut WITH a fade-in on the head of the first region and a
     * fade-out on the tail of the last one. See "Fades" above for how.
     *
     * Throws (code FADE_*) when the stream, the encoder or the decoder does
     * not allow a verified result; the caller then takes its decode/encode
     * lane, which fades the samples of the whole file.
     *
     * @param {Blob} blob - input MP3
     * @param {Array<{start:number,end:number}>} keepRegions
     * @param {object} opts - {fadeInSec, fadeOutSec, slug, onProgress, fadeCodec}
     * @returns {Promise<{blob:Blob, regionsCopied:number, method:string, fadeIn:object|null, fadeOut:object|null}>}
     */
    async function mp3FrameMultiTrimWithFades(blob, keepRegions, opts) {
        opts = opts || {};
        var fadeIn = Math.max(0, Number(opts.fadeInSec) || 0);
        var fadeOut = Math.max(0, Number(opts.fadeOutSec) || 0);
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
        function emit(r, stage) { if (onProgress) { try { onProgress(r, stage); } catch (_) {} } }

        // No fades requested — equivalent to plain multi-trim. Pass through
        // the progress callback so the caller sees the same shape.
        if (fadeIn === 0 && fadeOut === 0) {
            return mp3FrameMultiTrim(blob, keepRegions, { onProgress: onProgress });
        }

        if (!Array.isArray(keepRegions) || keepRegions.length === 0) {
            throw new Error('mp3-frame-trim: keepRegions array required');
        }
        for (var kr = 0; kr < keepRegions.length; kr++) {
            var r = keepRegions[kr];
            if (typeof r.start !== 'number' || typeof r.end !== 'number'
                || r.start < 0 || r.end <= r.start) {
                throw new Error('mp3-frame-trim: invalid region at index ' + kr);
            }
            if (kr > 0 && r.start < keepRegions[kr - 1].end) {
                throw new Error('mp3-frame-trim: regions overlap or unsorted at index ' + kr);
            }
        }
        var codec = opts.fadeCodec || webFadeCodec();
        if (!codec) throw fadeError('FADE_NO_CODEC', 'fade needs a decoder and MP3Encoder');

        // indexBlob takes the lion's share of wall time on big files: 0–70 %.
        var idx = await indexBlob(blob, {
            onProgress: onProgress ? function (ratio, stage) { onProgress(ratio * 0.70, stage || 'index'); } : null,
        });

        // Per-region frame ranges (same as multi-trim).
        var perRegion = [];
        for (var k2 = 0; k2 < keepRegions.length; k2++) {
            var reg = keepRegions[k2];
            var clampedStart = Math.max(0, Math.min(reg.start, idx.totalAudioDuration));
            var clampedEnd = Math.max(clampedStart, Math.min(reg.end, idx.totalAudioDuration));
            if (clampedEnd <= clampedStart) continue;
            try {
                var rng = frameRangeFor(idx, clampedStart, clampedEnd);
                perRegion.push({
                    startIdx: rng.startIdx, endIdx: rng.endIdx,
                    actualStart: idx.frames[rng.startIdx].cumStart,
                    actualEnd: (rng.endIdx < idx.frames.length)
                        ? idx.frames[rng.endIdx].cumStart
                        : idx.totalAudioDuration,
                });
            } catch (_) { continue; }
        }
        if (perRegion.length === 0) {
            throw new Error('mp3-frame-trim: all regions empty after clamp');
        }

        // Clamp fades to region durations — no point in 5 s fade-in on 1 s region.
        var first = perRegion[0], last = perRegion[perRegion.length - 1];
        fadeIn = Math.min(fadeIn, first.actualEnd - first.actualStart);
        fadeOut = Math.min(fadeOut, last.actualEnd - last.actualStart);

        var hdr = parseFrameHeader(idx.bytes, idx.frames[first.startIdx].offset);
        if (!hdr || hdr.layer !== 'Layer3') throw fadeError('FADE_NOT_ELIGIBLE', 'fade: not a Layer III stream');
        var per = 1152 / hdr.samples;          // 1 for MPEG-1, 2 for the half-size frames of MPEG-2/2.5
        var k = {
            hdr: hdr, spf: hdr.samples, sr: hdr.sampleRate, nch: hdr.channelMode === 3 ? 1 : 2,
            slug: opts.slug || 'mp3-frame-trim',
            cand: FADE_CAND, warm: FADE_WARM * per, look: FADE_LOOK * per, pre: FADE_PRE * per,
            tail: FADE_TAIL * per, before: FADE_CHECK_BEFORE * per, after: FADE_CHECK_AFTER * per,
        };
        emit(0.72, 'fade');

        var spf = k.spf, A = first.startIdx, B0 = first.endIdx, An = last.startIdx, Bn = last.endIdx;
        var NFI = Math.round(fadeIn * k.sr), NFO = Math.round(fadeOut * k.sr);
        var nFi = NFI > 0 ? Math.ceil(NFI / spf) : 0, nFo = NFO > 0 ? Math.ceil(NFO / spf) : 0;
        var single = first === last;
        var inEnd = Math.min(B0, A + nFi);          // first frame clear of the fade-in ramp
        var outStart = Math.max(An, Bn - nFo);      // first frame under the fade-out ramp

        // The ramp is laid over the slots of the OUTPUT's frames: that is what a
        // player decodes. Outside the region being kept the gain is 0 — a cut is a cut.
        function gainFor(region) {
            var lo = spf * region.startIdx, hi = spf * region.endIdx;
            var rampIn = region === first && NFI > 0, rampOut = region === last && NFO > 0;
            var inFrom = spf * A, outTo = spf * Bn;
            return function (G) {
                if (G < lo || G >= hi) return 0;
                var g = 1;
                if (rampIn) g = Math.min(g, (G - inFrom) / NFI);
                if (rampOut) g = Math.min(g, (outTo - G) / NFO);
                return g > 0 ? g : 0;
            };
        }
        // The frames we decode and replace must all be the stream the first one is.
        function assertStream(a, b) {
            for (var f = Math.max(idx.firstAudioFrame, a); f < Math.min(idx.frames.length, b); f++) {
                var h = parseFrameHeader(idx.bytes, idx.frames[f].offset);
                if (!h || h.layer !== 'Layer3' || !sameStream(h, hdr)) {
                    throw fadeError('FADE_NOT_ELIGIBLE', 'fade: the stream changes at frame ' + f);
                }
            }
        }
        /* The bitrate our frames are encoded at: one and a half times that of
           the frames they replace, on the next step of the table. Encoding what
           a codec has already encoded adds a second round of its noise. At the
           SAME bitrate that round is as loud as the first; a step and a half up
           it sits about 10 dB lower — under what the file already carries.
           MEASURED on one song, error of our frames against the source's own
           samples: 128 → 128 kbps −17 dB, 128 → 192 −27; 192 → 192 −25,
           192 → 320 −37. At 320 there is no step up and none is needed: the
           same quantiser steps come out again (−50 dB). */
        function bitrateFor(a, b) {
            var sum = 0, n = 0;
            for (var f = a; f < b; f++) {
                var h = parseFrameHeader(idx.bytes, idx.frames[f].offset);
                if (h) { sum += h.bitrate; n++; }
            }
            var table = BITRATE[hdr.version + '_Layer3'], want = 1.5 * (n ? sum / n : hdr.bitrate);
            for (var i = 1; i < 15; i++) if (table[i] >= want) return table[i];
            return table[14];
        }
        // One encode of a whole region: no seam inside it.
        async function wholeRegion(region, next) {
            assertStream(region.startIdx - k.pre, region.endIdx + 1);
            var dec = await fadeDecode(idx, codec, k, region.startIdx - 1, region.endIdx + 1);
            var plan = { first: region.startIdx, count: region.endIdx - region.startIdx, warm: 1 };
            var gain = gainFor(region), kbps = bitrateFor(region.startIdx, region.endIdx);
            var enc = await fadeEncode(codec, k, dec, plan, kbps, gain);
            var sealed = null;
            if (next != null) {
                sealed = await fadeSeal(idx, k, enc, region.endIdx - 1, next, function (lower, lastF) {
                    return fadeEncode(codec, k, dec, fadeTailPlan(k, plan, lastF), lower, gain);
                });
                if (!sealed) throw fadeError('FADE_NO_SEAM', 'fade: no room for the next frame\'s reservoir');
            }
            return {
                seg: fadeSegment(enc, region.startIdx, region.endIdx, sealed),
                info: { frames: plan.count, extFrames: 0, kbps: enc.kbps, shift: enc.shift, level: enc.level, seal: sealed ? sealed.how : 'none', sealKbps: sealed ? sealed.kbps : null, sealFrames: sealed ? sealed.frames.length : 0, check: null },
            };
        }

        var inChunk = null, outChunk = null, merged = false;
        var inMax = B0 - 1, outMin = An + 1;        // a seam needs a source frame on its far side
        if (single && nFi && nFo) {
            var gap = outStart - inEnd;
            if (gap < FADE_MERGE * per) {
                merged = true;                      // the ramps meet: the region is one encode
            } else {
                var mid = inEnd + (gap >> 1);
                inMax = Math.min(inMax, mid);
                outMin = Math.max(outMin, mid + 1);
            }
        }

        if (merged) {
            inChunk = await wholeRegion(first, null);
            inChunk.to = Bn;
        } else {
            if (nFi) {
                if (inEnd > inMax) {
                    // The ramp fills the first region: all of it is ours, and what
                    // follows it (the next region, across a cut) reads from our last frame.
                    inChunk = await wholeRegion(first, single ? null : perRegion[1].startIdx);
                    inChunk.to = B0;
                } else {
                    inMax = Math.min(inMax, inEnd + k.cand);
                    assertStream(A - k.pre, inMax + k.look + k.after + 2);
                    var decIn = await fadeDecode(idx, codec, k, A - 1, inMax + k.look + k.after + 2);
                    var planIn = { first: A, count: inMax - A, warm: 1 };
                    var gainIn = gainFor(first), kbpsIn = bitrateFor(A, inMax);
                    var encIn = await fadeEncode(codec, k, decIn, planIn, kbpsIn, gainIn);
                    emit(0.82, 'fade');
                    // Seams our last frame and the source's next frame agree on, the
                    // one whose source frame reads back least first.
                    var cands = [];
                    for (var f1 = inEnd; f1 <= inMax; f1++) {
                        if (!seamBlocksOk(encIn.bytes, fadeFrameAt(encIn, f1 - 1).offset, idx.bytes, idx.frames[f1].offset)) continue;
                        cands.push({ f: f1, need: frameMainDataBegin(idx.bytes, idx.frames[f1].offset) });
                    }
                    cands.sort(function (x, y) { return (x.need - y.need) || (x.f - y.f); });
                    var sealIn = null, seamIn = -1;
                    var reencodeIn = function (lower, lastF) {
                        return fadeEncode(codec, k, decIn, fadeTailPlan(k, planIn, lastF), lower, gainIn);
                    };
                    for (var ci = 0; ci < cands.length && ci < 3 && !sealIn; ci++) {
                        sealIn = await fadeSeal(idx, k, encIn, cands[ci].f - 1, cands[ci].f, reencodeIn);
                        if (sealIn) seamIn = cands[ci].f;
                    }
                    if (!sealIn) throw fadeError('FADE_NO_SEAM', 'fade: no seam after the fade-in (' + cands.length + ' candidates)');
                    var segIn = fadeSegment(encIn, A, seamIn, sealIn);
                    var vFrom = Math.max(A, seamIn - k.before), vTo = Math.min(B0, seamIn + k.after);
                    var vHead = segIn.raw.subarray(segIn.offsets[vFrom - A]);
                    var vTail = idx.bytes.subarray(idx.frames[seamIn].offset,
                        vTo < idx.frames.length ? idx.frames[vTo].offset : idx.fileEnd);
                    var vBytes = new Uint8Array(vHead.length + vTail.length);
                    vBytes.set(vHead, 0);
                    vBytes.set(vTail, vHead.length);
                    var checkIn = await fadeVerify(codec, k, decIn, {
                        bytes: vBytes, lead: 0, from: vFrom, to: vTo, seam: seamIn,
                        oursFrom: A, oursTo: seamIn, gainAt: gainIn, shift: encIn.shift,
                    });
                    inChunk = {
                        seg: segIn, to: seamIn,
                        info: { frames: seamIn - A, extFrames: seamIn - inEnd, kbps: encIn.kbps, shift: encIn.shift, level: encIn.level, seal: sealIn.how, sealKbps: sealIn.kbps, sealFrames: sealIn.frames.length, check: checkIn },
                    };
                }
            }
            emit(0.86, 'fade');
            if (nFo) {
                if (outStart < outMin) {
                    outChunk = await wholeRegion(last, null);
                    outChunk.from = An;
                } else {
                    outMin = Math.max(outMin, outStart - k.cand);
                    var warm = Math.min(k.warm, outMin - An);
                    assertStream(outMin - warm - k.before - k.pre, Bn + 1);
                    var decOut = await fadeDecode(idx, codec, k, outMin - warm - k.before, Bn + 1);
                    var planOut = { first: outMin, count: Bn - outMin, warm: warm };
                    var gainOut = gainFor(last);
                    var encOut = await fadeEncode(codec, k, decOut, planOut, bitrateFor(outMin, Bn), gainOut);
                    emit(0.94, 'fade');
                    var seamOut = -1;
                    for (var f0 = outStart; f0 >= outMin; f0--) {
                        if (seamBlocksOk(idx.bytes, idx.frames[f0 - 1].offset, encOut.bytes, fadeFrameAt(encOut, f0).offset)) { seamOut = f0; break; }
                    }
                    if (seamOut < 0) throw fadeError('FADE_NO_SEAM', 'fade: no seam before the fade-out');
                    var segOut = fadeSegment(encOut, seamOut, Bn, null);
                    var wFrom = Math.max(An, seamOut - k.before), wTo = Math.min(Bn, seamOut + k.after);
                    var wHead = contextSlice(idx, wFrom, seamOut);
                    var wTail = segOut.raw.subarray(0, wTo < Bn ? segOut.offsets[wTo - seamOut] : segOut.bytes);
                    var wBytes = new Uint8Array(wHead.bytes.length + wTail.length);
                    wBytes.set(wHead.bytes, 0);
                    wBytes.set(wTail, wHead.bytes.length);
                    var checkOut = await fadeVerify(codec, k, decOut, {
                        bytes: wBytes, lead: wHead.lead, from: wFrom, to: wTo, seam: seamOut,
                        oursFrom: seamOut, oursTo: Bn, gainAt: gainOut, shift: encOut.shift,
                    });
                    outChunk = {
                        seg: segOut, from: seamOut,
                        info: { frames: Bn - seamOut, extFrames: outStart - seamOut, kbps: encOut.kbps, shift: encOut.shift, level: encOut.level, seal: 'none', sealKbps: null, sealFrames: 0, check: checkOut },
                    };
                }
            }
        }

        // Output: our frames where the ramps are, the source's own everywhere else.
        var segs = [];
        for (var p = 0; p < perRegion.length; p++) {
            var pr = perRegion[p], from = pr.startIdx, to = pr.endIdx;
            if (p === 0 && inChunk) { segs.push(inChunk.seg); from = inChunk.to; }
            if (p === perRegion.length - 1 && outChunk) to = outChunk.from;
            if (to > from) segs.push(srcSegment(idx, from, to));
            if (p === perRegion.length - 1 && outChunk) segs.push(outChunk.seg);
        }
        var built = assembleMp3(idx, segs);
        emit(1.0, 'done');

        var inInfo = inChunk ? inChunk.info : null, outInfo = merged ? inInfo : (outChunk ? outChunk.info : null);
        function report(requested, applied, info) {
            return {
                requestedSec: requested, appliedSec: applied, method: 'pcm_ramp',
                framesEncoded: info.frames, extFrames: info.extFrames, kbps: info.kbps, level: info.level,
                seal: info.seal, sealKbps: info.sealKbps, sealFrames: info.sealFrames, check: info.check,
            };
        }
        return {
            blob: built.blob,
            headerWritten: built.headerWritten,
            splices: built.splices,
            regionsCopied: perRegion.length,
            method: 'pcm_ramp',
            shift: (inInfo || outInfo).shift,
            fadeIn: (fadeIn > 0 && inInfo) ? report(opts.fadeInSec, fadeIn, inInfo) : null,
            fadeOut: (fadeOut > 0 && outInfo) ? report(opts.fadeOutSec, fadeOut, outInfo) : null,
        };
    }

    /**
     * Lossless N-repeat — trim a region once, concat N copies into output.
     *
     * Replaces the OfflineAudioContext-based repeat path in audio-looper for
     * mp3→mp3 simple repeats. Output is bit-identical to playing the source
     * region back-to-back N times. Memory: O(file_size) — read source once,
     * write frame slices N times. Works on any device for any (file, N).
     *
     * @param {Blob} blob - input MP3
     * @param {object} opts - {start, end, repeats}
     * @returns {Promise<{blob:Blob, repeats:number, framesPerCopy:number, totalFrames:number}>}
     */
    async function mp3FrameRepeat(blob, opts) {
        opts = opts || {};
        var startSec = Number(opts.start) || 0;
        var endSec = Number(opts.end);
        var n = parseInt(opts.repeats, 10);
        if (!isFinite(endSec) || endSec <= startSec) {
            throw new Error('mp3-frame-trim.repeat: invalid time range');
        }
        if (!isFinite(n) || n < 1) {
            throw new Error('mp3-frame-trim.repeat: repeats must be ≥ 1');
        }

        var idx = await indexBlob(blob);
        if (startSec >= idx.totalAudioDuration) {
            throw new Error('mp3-frame-trim.repeat: start beyond duration');
        }
        var clampedEnd = Math.min(endSec, idx.totalAudioDuration);
        var rng = frameRangeFor(idx, startSec, clampedEnd);

        // One segment descriptor for the region — Blob.slice is zero-copy in
        // most browsers (it's a view). Repeating the same descriptor N times
        // doesn't N× the underlying byte storage, and the assembler counts the
        // frames N times so the header declares the repeated length, not one
        // pass of it.
        var regionSeg = srcSegment(idx, rng.startIdx, rng.endIdx);
        var repSegs = [];
        for (var i = 0; i < n; i++) repSegs.push(regionSeg);
        var repBuilt = assembleMp3(idx, repSegs);

        return {
            blob: repBuilt.blob,
            headerWritten: repBuilt.headerWritten,
            splices: repBuilt.splices,
            repeats: n,
            framesPerCopy: rng.endIdx - rng.startIdx,
            // What was written: a seam moved to the neighbouring pair of frames
            // (repairSplices) takes a frame or two out of the count.
            totalFrames: repBuilt.frameCount,
            actualStart: idx.frames[rng.startIdx].cumStart,
            actualEnd: (rng.endIdx < idx.frames.length)
                ? idx.frames[rng.endIdx].cumStart
                : idx.totalAudioDuration,
        };
    }

    window.Mp3FrameTrim = {
        trim: mp3FrameTrim,
        multiTrim: mp3FrameMultiTrim,
        multiTrimWithFades: mp3FrameMultiTrimWithFades,
        repeat: mp3FrameRepeat,
        parseFrameHeader: parseFrameHeader,
        parseId3v2Length: parseId3v2Length,
        indexFrames: indexFrames,
        /* Экспортировано 2026-09-10 для /car-usb-music: там тот же вопрос
           «есть ли у этого потока заголовок VBR», и вторая копия сканера
           разошлась бы с этой ровно на VBRI (её проглядела и эта, пока не
           поймали на корпусе). Одна реализация — один ответ. */
        isXingFrame: isXingFrame,
        // Exported so any future MP3 splicer declares its length the same way
        // instead of re-learning why players guessed it wrong.
        buildXingHeaderFrame: buildXingHeaderFrame,
        /* The stream verdict (2026-09-23). Every "is this MP3?" on the site
           asks these instead of sniffing two bytes: sniffMpeg(blob) before a
           lossless lane, locateStream / looksLikeMpegStream over bytes in hand. */
        locateStream: locateStream,
        looksLikeMpegStream: looksLikeMpegStream,
        sniffMpeg: sniffMpeg,
    };
})();
